/**
 * Message Templates — Catalogue narratif pour la Messagerie HST.
 *
 * Chaque template définit :
 *   - id : identifiant unique
 *   - senderType : 'driver' | 'client' | 'supplier' | 'bank' | 'competitor' |
 *                  'urssaf' | 'media' | 'inspector' | 'hr' | 'system' | 'news'
 *   - tone : 'friendly' | 'formal' | 'aggressive' | 'humour' | 'urgent' | 'neutral'
 *   - kind : 'info' | 'decision'
 *   - weight : poids de tirage aléatoire (templates random uniquement)
 *   - trigger : 'random' | 'event:<id>' | 'driver:levelup' | 'driver:hired' |
 *               'contract:won' | 'milestone:<key>' | 'manual'
 *   - ttlDays : durée de validité (sinon défaut = 7 j)
 *   - subjectTemplate / bodyTemplate : strings avec placeholders {placeholder}
 *   - choices? : décisions (kind === 'decision'). Chaque choix porte un id,
 *                un label et un tableau d'effets { kind, amount, target? }.
 *   - defaultChoiceId? : choix appliqué automatiquement à l'expiration
 *   - minPhase?, minDays?, maxDays? : conditions d'apparition
 *
 * Placeholders disponibles à l'instanciation :
 *   {companyName}  {playerName}  {driverName}  {driverFirstName}
 *   {clientName}   {amount}      {percent}     {city}
 *   {vehicleModel} {month}       {year}        {day}
 */
export const MESSAGE_TEMPLATES = [
  // ───────────────────────────────────────────────────────────────────────────
  // SALARIÉS / CHAUFFEURS (45+ templates)
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'drv-raise-01', senderType: 'driver', tone: 'formal', kind: 'decision',
    trigger: 'random', weight: 5, ttlDays: 6, minDays: 60,
    subjectTemplate: 'Demande d\'augmentation — {driverFirstName}',
    bodyTemplate:
      "Bonjour,\n\nCela fait maintenant plusieurs mois que je travaille pour " +
      "{companyName} et je pense avoir prouvé mon engagement. Je souhaite " +
      "discuter d'une revalorisation salariale (+150 €/mois).\n\nDans " +
      "l'attente,\n{driverName}",
    choices: [
      { id: 'accept', label: 'Accorder +150 €/mois', effects: [{ kind: 'driverRaise', amount: 150 }, { kind: 'driverSatisfaction', amount: 12 }] },
      { id: 'partial', label: 'Proposer +75 €/mois',  effects: [{ kind: 'driverRaise', amount: 75  }, { kind: 'driverSatisfaction', amount: 4  }] },
      { id: 'refuse', label: 'Refuser',               effects: [{ kind: 'driverSatisfaction', amount: -15 }] },
    ],
    defaultChoiceId: 'refuse',
  },
  {
    id: 'drv-illness-01', senderType: 'driver', tone: 'urgent', kind: 'info',
    trigger: 'random', weight: 4, ttlDays: 2,
    subjectTemplate: 'Arrêt maladie — {driverFirstName}',
    bodyTemplate:
      "Désolé pour le dérangement, je dois m'arrêter quelques jours pour " +
      "un problème de santé (mal de dos). Je serai de retour rapidement.\n\n" +
      "{driverName}",
  },
  {
    id: 'drv-training-req-01', senderType: 'driver', tone: 'friendly', kind: 'decision',
    trigger: 'random', weight: 4, ttlDays: 5, minDays: 30,
    subjectTemplate: 'J\'aimerais évoluer — formation possible ?',
    bodyTemplate:
      "Bonjour,\n\nJe me sens un peu à l'étroit dans mon poste actuel. " +
      "Pourriez-vous m'inscrire à une formation complémentaire ? Je suis " +
      "particulièrement intéressé(e) par l'éco-conduite ou une spécialité.\n\n" +
      "Merci de votre attention,\n{driverName}",
    choices: [
      { id: 'eco',    label: 'Éco-conduite (800 €)',    effects: [{ kind: 'driverSatisfaction', amount: 8 }, { kind: 'treasury', amount: -800 }] },
      { id: 'later',  label: 'Plus tard',                effects: [{ kind: 'driverSatisfaction', amount: -3 }] },
      { id: 'noway',  label: "Pas le budget pour l'instant", effects: [{ kind: 'driverSatisfaction', amount: -6 }] },
    ],
    defaultChoiceId: 'later',
  },
  {
    id: 'drv-conflict-01', senderType: 'driver', tone: 'aggressive', kind: 'decision',
    trigger: 'random', weight: 3, ttlDays: 4, minDays: 45,
    subjectTemplate: 'Tensions dans l\'équipe',
    bodyTemplate:
      "Patron,\n\nJ'ai un problème avec un collègue qui ne respecte pas les " +
      "consignes de sécurité. Si rien ne bouge, je vais devoir partir.\n\n" +
      "Merci d'agir,\n{driverName}",
    choices: [
      { id: 'mediation', label: 'Organiser une médiation (300 €)',  effects: [{ kind: 'treasury', amount: -300 }, { kind: 'driverSatisfaction', amount: 8 }] },
      { id: 'side',      label: 'Le soutenir explicitement',         effects: [{ kind: 'driverSatisfaction', amount: 12 }, { kind: 'reputation', amount: -2 }] },
      { id: 'ignore',    label: 'Ne pas s\'en mêler',                effects: [{ kind: 'driverSatisfaction', amount: -10 }] },
    ],
    defaultChoiceId: 'ignore',
  },
  {
    // Pushed by coreSlice.nextDay when an active driver's daysToCritical
    // projection falls into the 5-9 days window. 30-day cooldown per driver.
    id: 'drv-burnout-alert-01', senderType: 'driver', tone: 'urgent', kind: 'decision',
    trigger: 'system:burnout-warning', weight: 0, ttlDays: 5,
    subjectTemplate: 'Je commence à fatiguer — peut-on parler ?',
    bodyTemplate:
      "Bonjour,\n\nJe voulais vous écrire à titre personnel. Ces dernières " +
      "semaines, ma motivation s'effrite. La mission actuelle ne me " +
      "correspond plus vraiment et je sens que je perds en énergie chaque " +
      "jour.\n\nSi rien ne change, je crains de ne plus pouvoir tenir " +
      "longtemps. Pourriez-vous m'aider à trouver une solution ?\n\n" +
      "Cordialement,\n{driverName}",
    choices: [
      // Reassign: stop current mission, +5 sat, no cash cost.
      { id: 'reassign', label: 'Réassigner sur une autre mission', effects: [{ kind: 'driverUnassign' }, { kind: 'driverSatisfaction', amount: 5 }] },
      // Bonus: -500 € treasury, +15 sat (handled by driverBonus kind).
      { id: 'bonus', label: 'Prime exceptionnelle (-500 €)', effects: [{ kind: 'driverBonus', amount: 500 }] },
      // Talk only: +5 sat, no cost.
      { id: 'listen', label: 'Écouter (RH dédiée)', effects: [{ kind: 'driverSatisfaction', amount: 5 }] },
    ],
    defaultChoiceId: 'listen',
  },
  {
    id: 'drv-thanks-01', senderType: 'driver', tone: 'friendly', kind: 'info',
    trigger: 'driver:levelup', weight: 0, ttlDays: 30,
    subjectTemplate: 'Merci pour la confiance',
    bodyTemplate:
      "Bonjour,\n\nJe voulais vous remercier — je sens que je progresse et " +
      "que mon travail est reconnu. Niveau {driverLevel} atteint, je suis " +
      "fier(e) de faire partie de {companyName}.\n\nÀ bientôt sur la route,\n{driverName}",
  },
  {
    // 11/02/2026 (Lot N suite 2) — Message de remerciement après augmentation
    // ACCEPTÉE. Variante « ambitieux » : tonalité performance / engagement.
    // Pushed by messageSlice.applyMessageChoice après détection d'une hausse
    // effective de salaryGross. Info-only (pas de décision).
    id: 'drv-thanks-after-raise-ambitieux', senderType: 'driver', tone: 'friendly', kind: 'info',
    trigger: 'system:raise-accepted', weight: 0, ttlDays: 14,
    subjectTemplate: 'Merci pour la confiance — je vais redoubler d\'efforts',
    bodyTemplate:
      "Bonjour,\n\nMerci infiniment pour cette revalorisation. Cette reconnaissance " +
      "me motive à viser encore plus haut : je vais redoubler d'efforts pour " +
      "mériter cette confiance et faire progresser {companyName} avec moi.\n\n" +
      "Vous pouvez compter sur mon engagement total — prochain palier d'attente " +
      "au plus tôt dans 6 mois.\n\nBien à vous,\n{driverName}",
  },
  {
    // 11/02/2026 (Lot N suite 2) — Variante « fier » : tonalité reconnaissance
    // méritée, rappel du cadre temporel.
    id: 'drv-thanks-after-raise-fier', senderType: 'driver', tone: 'formal', kind: 'info',
    trigger: 'system:raise-accepted', weight: 0, ttlDays: 14,
    subjectTemplate: 'Bien noté — la reconnaissance que je méritais',
    bodyTemplate:
      "Bonjour,\n\nJ'ai bien noté votre décision. C'est la reconnaissance que je " +
      "méritais après tout ce travail. Merci d'avoir vu la valeur ajoutée que " +
      "j'apporte à {companyName}.\n\nJe sais que mon prochain palier d'attente " +
      "est de 6 mois — je tiendrai cet engagement professionnel.\n\n" +
      "Cordialement,\n{driverName}",
  },
  {
    // 11/02/2026 (Lot N suite 2) — Variante par défaut (loyal / pragmatique /
    // autres traits). Tonalité simple, sincère, fidélité à l'entreprise.
    id: 'drv-thanks-after-raise-loyal', senderType: 'driver', tone: 'friendly', kind: 'info',
    trigger: 'system:raise-accepted', weight: 0, ttlDays: 14,
    subjectTemplate: 'Merci sincèrement — je reste fidèle à {companyName}',
    bodyTemplate:
      "Bonjour,\n\nMerci sincèrement pour cette revalorisation. Ça compte beaucoup " +
      "pour moi et ma famille. Je reste fidèle à {companyName} et je continuerai " +
      "à donner le meilleur sur la route.\n\nProchaine demande au plus tôt dans " +
      "6 mois — vous n'entendrez plus parler de salaire d'ici là, promis.\n\n" +
      "Bien à vous,\n{driverName}",
  },
  {
    // Pushed by hrSlice.addDailyXp when a driver levels up — probability 35 %
    // (60 % for 'ambitieux' drivers). Decision with 3 outcomes.
    id: 'drv-raise-after-levelup-01', senderType: 'driver', tone: 'formal', kind: 'decision',
    trigger: 'driver:levelup', weight: 0, ttlDays: 5,
    subjectTemplate: 'Niveau {level} — revalorisation salariale ?',
    bodyTemplate:
      "Bonjour,\n\nJe viens d'atteindre le niveau {level} et je voulais vous " +
      "remercier pour la confiance. Compte tenu de mes nouvelles compétences " +
      "et responsabilités, accepteriez-vous d'envisager une revalorisation " +
      "de mon salaire ?\n\n+200 €/mois me semblerait juste — je reste " +
      "ouvert(e) à la discussion.\n\nCordialement,\n{driverName}",
    choices: [
      { id: 'accord_full', label: 'Accorder +200 €/mois', effects: [{ kind: 'driverRaise', amount: 200 }, { kind: 'driverSatisfaction', amount: 12 }] },
      // Counter-offer: half of the requested raise, with a real risk that the
      // driver refuses (and quits). Acceptance odds depend on personality
      // (pragmatique +15 %, fier/ambitieux −10 %).
      { id: 'counter_half', label: 'Contre-proposition : +100 €/mois (risque)', effects: [{ kind: 'driverCounterOffer', amount: 100, expected: 200 }] },
      { id: 'refuse', label: 'Refuser poliment', effects: [{ kind: 'driverSatisfaction', amount: -15 }] },
    ],
    defaultChoiceId: 'refuse',
  },
  {
    // Pushed by coreSlice when a driver's satisfaction crosses below 30 for
    // the first time in a 90-day window. Distinct from drv-burnout-alert-01
    // which fires earlier on projection. This one is the "I'm done, give me
    // a real raise or I leave" moment.
    id: 'drv-renegotiation-low-sat-01', senderType: 'driver', tone: 'urgent', kind: 'decision',
    trigger: 'system:low-satisfaction', weight: 0, ttlDays: 5,
    subjectTemplate: 'Renégociation salariale ou je pars',
    bodyTemplate:
      "Bonjour,\n\nJe vous écris parce que je suis arrivé(e) à un point de " +
      "rupture. Mon salaire ne correspond plus à ce que je donne à " +
      "{companyName} et plusieurs concurrents m'ont déjà approché(e).\n\n" +
      "Je demande une revalorisation de +300 €/mois pour rester. Sinon, je " +
      "donnerai ma démission cette semaine.\n\nCordialement,\n{driverName}",
    choices: [
      { id: 'accord_full', label: 'Accorder +300 €/mois', effects: [{ kind: 'driverRaise', amount: 300 }, { kind: 'driverSatisfaction', amount: 25 }] },
      { id: 'counter_half', label: 'Contre-proposition : +150 €/mois (risque)', effects: [{ kind: 'driverCounterOffer', amount: 150, expected: 300 }] },
      // 23/07/2026 (fix user prod « chauffeurs qui disparaissent ») —
      // avant : driverFire IMMÉDIAT, appliqué aussi par défaut à
      // l'expiration du message (5 j) → chauffeur supprimé sans bruit
      // (cas typique : email raté pendant un arrêt maladie). Désormais le
      // refus déclenche le préavis légal de 30 j, visible dans la
      // bannière RH, avec lettre de dernière chance (aligner / acter).
      { id: 'refuse', label: 'Refuser (il/elle déposera un préavis de 30 j)', effects: [{ kind: 'driverStartDeparture', amount: 300 }] },
    ],
    defaultChoiceId: 'refuse',
  },
  {
    // Pushed by eventSlice.resolveEvent when evt-rh-02 (arrêt maladie) fires.
    // Info-only message that mirrors the event in the inbox so the player has
    // a single feed of what's happening to their team.
    id: 'drv-illness-system-01', senderType: 'driver', tone: 'urgent', kind: 'info',
    trigger: 'event:evt-rh-02', weight: 0, ttlDays: 5,
    subjectTemplate: 'Arrêt maladie — {driverFirstName} ({days} jours, reprise J{returnDay})',
    bodyTemplate:
      "Bonjour,\n\nLe médecin m'a prescrit {days} jours d'arrêt suite à un " +
      "problème de santé. Reprise prévue le jour J{returnDay}. " +
      "Le véhicule reste immobilisé jusqu'à cette date.\n\n" +
      "Merci de votre compréhension,\n{driverName}",
  },
  {
    // Pushed by eventSlice.resolveEvent when evt-rh-02 choix 0 (intérimaire) :
    // le chauffeur est en arrêt mais sa mission continue via l'intérim.
    id: 'drv-illness-interim-01', senderType: 'driver', tone: 'urgent', kind: 'info',
    trigger: 'event:evt-rh-02', weight: 0, ttlDays: 5,
    subjectTemplate: 'Arrêt maladie — {driverFirstName} ({days} jours, intérim en place)',
    bodyTemplate:
      "Bonjour,\n\nLe médecin m'a prescrit {days} jours d'arrêt suite à un " +
      "problème de santé. Reprise prévue le jour J{returnDay}.\n\n" +
      "Bonne nouvelle : l'intérimaire de remplacement prend le relais dès " +
      "aujourd'hui — ma mission et le véhicule continuent de tourner " +
      "normalement pendant mon absence.\n\n" +
      "Merci de votre compréhension,\n{driverName}",
  },
  {
    // Pushed by coreSlice.nextDay when a driver returns from sick leave.
    // 1-day TTL — purely informational, no action required.
    id: 'drv-illness-return-01', senderType: 'driver', tone: 'friendly', kind: 'info',
    trigger: 'system', weight: 0, ttlDays: 2,
    subjectTemplate: 'Retour au travail — {driverFirstName}',
    bodyTemplate:
      "Bonjour,\n\nJe vous confirme ma reprise au travail aujourd'hui. " +
      "Je vais reprendre mes missions habituelles. Merci pour votre " +
      "patience pendant mon arrêt.\n\nBonne journée,\n{driverName}",
  },
  {
    id: 'drv-resignation-warn-01', senderType: 'driver', tone: 'formal', kind: 'decision',
    trigger: 'random', weight: 2, ttlDays: 5, minDays: 90,
    subjectTemplate: 'Je réfléchis à mon avenir',
    bodyTemplate:
      "Bonjour,\n\nJe vous écris avec une certaine hésitation. Un concurrent " +
      "m'a contacté avec une offre intéressante. Avant d'y répondre, je " +
      "voulais savoir si {companyName} envisage des évolutions pour moi.\n\n" +
      "Cordialement,\n{driverName}",
    choices: [
      { id: 'retain', label: 'Augmentation (+100 €) + formation', effects: [{ kind: 'driverRaise', amount: 100 }, { kind: 'driverSatisfaction', amount: 20 }] },
      { id: 'talk',   label: 'Discussion + reconnaissance verbale', effects: [{ kind: 'driverSatisfaction', amount: 5 }] },
      { id: 'letgo',  label: 'Le laisser partir s\'il veut',        effects: [{ kind: 'driverSatisfaction', amount: -15 }] },
    ],
    defaultChoiceId: 'letgo',
  },
  {
    id: 'drv-near-miss-01', senderType: 'driver', tone: 'urgent', kind: 'info',
    trigger: 'random', weight: 3, ttlDays: 1,
    subjectTemplate: 'Presque accident ce matin',
    bodyTemplate:
      "Patron, j'ai eu chaud — un automobiliste m'a déboîté sans clignotant. " +
      "Aucun dégât mais la fatigue n'aide pas. Je vais lever le pied.\n\n" +
      "{driverFirstName}",
  },
  {
    id: 'drv-vacation-01', senderType: 'driver', tone: 'friendly', kind: 'decision',
    trigger: 'random', weight: 4, ttlDays: 6,
    subjectTemplate: 'Demande de congés',
    bodyTemplate:
      "Bonjour,\n\nJ'aimerais poser 5 jours de congé dans 2 semaines pour " +
      "un événement familial. Je peux décaler si nécessaire.\n\n{driverName}",
    choices: [
      { id: 'grant', label: 'Accorder (5 jours)', effects: [{ kind: 'driverSatisfaction', amount: 8 }, { kind: 'driverVacation', days: 5 }] },
      // 08/02/2026 — Q2 prod bug : `1 500 €` flat retiré du label et de
      // l'effet `treasury`. Le coût réel est désormais calculé dans
      // messageSlice (`days × tarif intérim selon tier`), affiché dans
      // la modale de décision. Label garde une fourchette indicative
      // (≈ 600 € pour T1, ≈ 2 000 € pour T5 sur 5 jours).
      { id: 'subcontract', label: 'Accorder + sous-traiter (coût selon véhicule)', effects: [{ kind: 'driverSatisfaction', amount: 10 }, { kind: 'driverVacation', days: 5, subcontract: true }] },
      { id: 'shift', label: 'Décaler de 2 semaines', effects: [{ kind: 'driverSatisfaction', amount: 1 }] },
      { id: 'deny',  label: 'Refuser (charge de travail)', effects: [{ kind: 'driverSatisfaction', amount: -8 }] },
    ],
    defaultChoiceId: 'grant',
  },
  {
    id: 'drv-route-tip-01', senderType: 'driver', tone: 'humour', kind: 'info',
    trigger: 'random', weight: 4, ttlDays: 4,
    subjectTemplate: 'Anecdote de tournée',
    bodyTemplate:
      "Salut,\n\nAujourd'hui j'ai vu un camion d'un concurrent en panne sur " +
      "l'A6, on dirait qu'ils ne révisent pas grand-chose chez eux ! Petit " +
      "encouragement à continuer notre boulot de pros 😉\n\n{driverFirstName}",
  },
  {
    id: 'drv-equipment-01', senderType: 'driver', tone: 'formal', kind: 'decision',
    trigger: 'random', weight: 3, ttlDays: 5, minDays: 60,
    subjectTemplate: 'Matériel à renouveler',
    bodyTemplate:
      "Bonjour,\n\nMon GPS commence à faire des siennes (réacheminement " +
      "fantaisiste, écran qui faiblit). Pourriez-vous prévoir un renouvellement ?\n\n" +
      "{driverName}",
    choices: [
      { id: 'replace', label: 'Renouveler le GPS (250 €)', effects: [{ kind: 'treasury', amount: -250 }, { kind: 'driverSatisfaction', amount: 4 }] },
      { id: 'wait',    label: 'Attendre la fin du mois',     effects: [{ kind: 'driverSatisfaction', amount: -2 }] },
    ],
    defaultChoiceId: 'wait',
  },
  {
    id: 'drv-uniform-01', senderType: 'driver', tone: 'neutral', kind: 'info',
    trigger: 'random', weight: 3, ttlDays: 4,
    subjectTemplate: 'Tenues de travail usées',
    bodyTemplate:
      "Je signale que plusieurs collègues ont des polos vraiment fatigués. " +
      "Ce serait bien d'en commander un lot.\n\n{driverFirstName}",
  },
  // 08/02/2026 — Q2 user prod : chauffeur prend l'initiative de demander
  // une formation (~1× tous les 90 j, weight 1). Très rare : c'est un
  // signal d'engagement du chauffeur (ambitieux ou consciencieux).
  // L'éco-conduite est choisie comme formation « universelle » (utile à
  // tout chauffeur, indépendant du permis / spécialité).
  {
    id: 'drv-self-training-request-01', senderType: 'driver', tone: 'formal', kind: 'decision',
    trigger: 'random', weight: 1, ttlDays: 7, minDays: 90,
    subjectTemplate: 'Demande de formation — Éco-conduite',
    bodyTemplate:
      "Bonjour,\n\nJ'aimerais beaucoup suivre la formation **éco-conduite** " +
      "(800 €, 3 jours). Je pense que je peux faire baisser notre consommation " +
      "de carburant de 5 à 10 %, et ça rejaillira sur les marges. Si vous " +
      "êtes d'accord, je m'y inscris.\n\nCordialement,\n{driverFirstName}",
    choices: [
      { id: 'fund',  label: 'Financer la formation (800 €, +0,2 compétence)', effects: [
        { kind: 'trainDriver', trainingType: 'ecoConduite' },
        { kind: 'driverSatisfaction', amount: 12 },
      ] },
      { id: 'defer', label: 'Pas maintenant', effects: [
        { kind: 'driverSatisfaction', amount: -3 },
      ] },
    ],
    defaultChoiceId: 'defer',
  },
  // 03/06/2026 — Supprimé : le template 'drv-mileage-record-01' (350 000 km)
  // était purement décoratif et jamais conditionné au km réel des véhicules,
  // ce qui le rendait incohérent en début de partie (J4, camion neuf).
  // Si on veut un jour fêter un cap de kilométrage, il faudra le déclencher
  // via un trigger 'condition' qui inspecte state.vehicles.
  {
    id: 'drv-anniversary-01', senderType: 'driver', tone: 'friendly', kind: 'info',
    trigger: 'random', weight: 2, ttlDays: 5, minDays: 360,
    subjectTemplate: 'Un an chez {companyName} !',
    bodyTemplate:
      "Bonjour,\n\nÇa fait un an aujourd'hui que je suis chez {companyName}. " +
      "Sincèrement, je suis content d'être là. Merci pour la confiance.\n\n" +
      "{driverName}",
  },

  // ───────────────────────────────────────────────────────────────────────────
  // CLIENTS (30+ templates)
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'cli-praise-01', senderType: 'client', tone: 'friendly', kind: 'info',
    trigger: 'random', weight: 4, ttlDays: 7,
    subjectTemplate: 'Excellent travail !',
    bodyTemplate:
      "Bonjour,\n\nJe tenais à vous féliciter pour la dernière prestation. " +
      "Le chauffeur était impeccable, ponctuel, courtois. C'est rare !\n\n" +
      "Cordialement,\n{clientName}",
  },
  {
    id: 'cli-complaint-01', senderType: 'client', tone: 'aggressive', kind: 'decision',
    trigger: 'random', weight: 4, ttlDays: 3,
    subjectTemplate: 'Plainte — livraison défectueuse',
    bodyTemplate:
      "Bonjour,\n\nNotre livraison est arrivée avec 2 heures de retard et " +
      "deux palettes endommagées. C'est inacceptable. Quelle est votre " +
      "réponse ?\n\n{clientName}",
    choices: [
      { id: 'compensate', label: 'Avoir 800 € + excuses',     effects: [{ kind: 'treasury', amount: -800 }, { kind: 'reputation', amount: 3 }] },
      { id: 'discount',   label: 'Remise 15% sur prochain contrat', effects: [{ kind: 'reputation', amount: 1 }] },
      { id: 'deny',       label: 'Contester (pas notre faute)', effects: [{ kind: 'reputation', amount: -5 }] },
    ],
    defaultChoiceId: 'compensate',
  },
  {
    id: 'cli-renewal-01', senderType: 'client', tone: 'formal', kind: 'decision',
    trigger: 'random', weight: 5, ttlDays: 5, minDays: 90,
    subjectTemplate: 'Renouvellement de contrat',
    bodyTemplate:
      "Bonjour,\n\nNotre contrat actuel arrive à échéance. Nous sommes prêts " +
      "à renouveler pour 12 mois, mais nous attendons une remise commerciale " +
      "de 5%.\n\nVotre décision ?\n\n{clientName}",
    choices: [
      { id: 'accept',  label: 'Accorder -5% (fidélisation)', effects: [{ kind: 'reputation', amount: 4 }, { kind: 'notoriety', amount: 2 }] },
      { id: 'counter', label: 'Contre-offre -2.5%',          effects: [{ kind: 'reputation', amount: 2 }] },
      { id: 'refuse',  label: 'Refuser la remise',           effects: [{ kind: 'reputation', amount: -4 }] },
    ],
    defaultChoiceId: 'counter',
  },
  {
    id: 'cli-late-pay-01', senderType: 'client', tone: 'formal', kind: 'info',
    trigger: 'random', weight: 3, ttlDays: 4,
    subjectTemplate: 'Retard de paiement de notre part',
    bodyTemplate:
      "Bonjour,\n\nNous traversons une période de trésorerie difficile. " +
      "Le paiement de la dernière facture aura 15 jours de retard. Merci " +
      "pour votre compréhension.\n\n{clientName}",
  },
  {
    id: 'cli-new-business-01', senderType: 'client', tone: 'friendly', kind: 'decision',
    trigger: 'random', weight: 4, ttlDays: 5, minDays: 30,
    subjectTemplate: 'Opportunité commerciale',
    bodyTemplate:
      "Bonjour,\n\nNous avons un besoin urgent de transport pour la semaine " +
      "prochaine. **Mobilisation d'1 camion + 1 chauffeur pendant 3 à 7 jours**, " +
      "tarif négocié à 2 500 € net (versés à la fin de la mission). " +
      "Êtes-vous intéressé ?\n\n" +
      "{clientName}",
    choices: [
      {
        id: 'accept',
        label: 'Accepter (mobilise 1 camion + 1 chauffeur)',
        effects: [{ kind: 'commercialOpportunity', treasuryReward: 2500, reputationReward: 2 }],
      },
      { id: 'study',  label: 'Étudier (réponse différée)', effects: [{ kind: 'notoriety', amount: 1 }] },
      { id: 'refuse', label: 'Refuser (capacité saturée)', effects: [{ kind: 'reputation', amount: -1 }] },
    ],
    defaultChoiceId: 'study',
  },
  {
    id: 'cli-defection-warn-01', senderType: 'client', tone: 'aggressive', kind: 'decision',
    trigger: 'random', weight: 3, ttlDays: 4, minDays: 120,
    subjectTemplate: 'Vos concurrents nous démarchent',
    bodyTemplate:
      "Bonjour,\n\nUn concurrent vient de nous faire une offre alléchante " +
      "(15% moins cher). Pour que nous restions chez vous, il faudrait " +
      "geler vos tarifs et améliorer le SLA.\n\n{clientName}",
    choices: [
      { id: 'match',   label: 'Geler tarifs + SLA renforcé', effects: [{ kind: 'reputation', amount: 6 }, { kind: 'treasury', amount: -500 }] },
      { id: 'partial', label: 'Geler tarifs seulement',      effects: [{ kind: 'reputation', amount: 2 }] },
      { id: 'letgo',   label: 'Le laisser partir',            effects: [{ kind: 'reputation', amount: -8 }, { kind: 'notoriety', amount: -3 }] },
    ],
    defaultChoiceId: 'letgo',
  },
  {
    id: 'cli-volume-up-01', senderType: 'client', tone: 'formal', kind: 'decision',
    trigger: 'random', weight: 3, ttlDays: 5, minDays: 60,
    subjectTemplate: 'Augmentation des volumes',
    bodyTemplate:
      "Notre activité explose. Nous voulons doubler les volumes confiés à " +
      "{companyName}. Pouvez-vous absorber ?\n\n{clientName}",
    choices: [
      { id: 'yes',     label: 'Oui, absorbez les volumes',  effects: [{ kind: 'treasury', amount: 4000 }, { kind: 'reputation', amount: 5 }] },
      { id: 'partial', label: 'Seulement +30% pour l\'instant', effects: [{ kind: 'treasury', amount: 1200 }, { kind: 'reputation', amount: 2 }] },
      { id: 'no',      label: 'Non, capacité insuffisante',  effects: [{ kind: 'reputation', amount: -3 }] },
    ],
    defaultChoiceId: 'partial',
  },
  {
    id: 'cli-press-mention-01', senderType: 'client', tone: 'friendly', kind: 'info',
    trigger: 'random', weight: 2, ttlDays: 7,
    subjectTemplate: 'Mention dans notre rapport annuel',
    bodyTemplate:
      "Bonjour,\n\nNous vous avons cité comme partenaire logistique " +
      "exemplaire dans notre rapport annuel. Bravo pour le travail !\n\n" +
      "{clientName}",
  },
  {
    id: 'cli-rfp-01', senderType: 'client', tone: 'formal', kind: 'decision',
    trigger: 'random', weight: 2, ttlDays: 6, minDays: 180,
    subjectTemplate: 'Appel d\'offres — réponse souhaitée',
    bodyTemplate:
      "Bonjour,\n\nNous lançons un AO pour un contrat à 6 chiffres. " +
      "Voulez-vous y répondre ? Cahier des charges joint.\n\n{clientName}",
    choices: [
      { id: 'go',    label: 'Y répondre (frais 500 €)',  effects: [{ kind: 'treasury', amount: -500 }, { kind: 'notoriety', amount: 4 }] },
      { id: 'pass',  label: 'Passer (trop chronophage)', effects: [] },
    ],
    defaultChoiceId: 'pass',
  },

  // ───────────────────────────────────────────────────────────────────────────
  // CONSTRUCTEURS / FOURNISSEURS (15+ templates)
  // ───────────────────────────────────────────────────────────────────────────

  // === Financement constructeur — system messages (triggered programmatically)
  {
    id: 'mfg-finance-confirm-01', senderType: 'supplier', tone: 'formal', kind: 'info',
    trigger: 'system', weight: 0, ttlDays: 14,
    subjectTemplate: '{brandName} — Confirmation de financement',
    bodyTemplate:
      "Bonjour,\n\nNous vous confirmons le financement de votre {vehicleName} :\n" +
      "  - Mensualité : {monthly} €\n" +
      "  - Durée : {durationMonths} mois\n\n" +
      "Votre véhicule est prêt à être livré. Merci de votre confiance.\n\n" +
      "L'équipe {brandName}",
  },
  {
    id: 'mfg-loyalty-upgrade-01', senderType: 'supplier', tone: 'friendly', kind: 'info',
    trigger: 'system', weight: 0, ttlDays: 14,
    subjectTemplate: '{brandName} — Vous passez au palier « {tierLabel} »',
    bodyTemplate:
      "Bonjour,\n\nNous tenons à vous remercier pour votre fidélité. Vous accédez " +
      "désormais au statut « {tierLabel} » chez {brandName}.\n\n" +
      "Vos nouveaux avantages :\n" +
      "  - Remise sur taux : -{discountPp} pp\n" +
      "  - Ristourne sur valeur résiduelle : {rebatePct} %\n\n" +
      "Au plaisir de continuer cette aventure ensemble.\n\n" +
      "L'équipe {brandName}",
  },
  {
    id: 'mfg-promo-01', senderType: 'supplier', tone: 'friendly', kind: 'info',
    trigger: 'random', weight: 3, ttlDays: 14,
    subjectTemplate: 'Opération constructeurs — Découvrez nos offres',
    bodyTemplate:
      "Bonjour,\n\nCe trimestre, nos partenaires constructeurs (VALREK, MANTOR, " +
      "IVECTRA, SCANEX, VOLVAX, DAFORA) proposent des conditions de financement " +
      "préférentielles. Si vous envisagez d'élargir votre flotte, c'est le bon moment.\n\n" +
      "Rendez-vous sur la page Flotte → Acheter → Financement constructeur.\n\n" +
      "{senderName}",
  },

  {
    id: 'sup-promo-01', senderType: 'supplier', tone: 'friendly', kind: 'info',
    trigger: 'random', weight: 4, ttlDays: 7,
    subjectTemplate: 'Promo véhicules — -8% ce mois',
    bodyTemplate:
      "Bonjour,\n\nOpération exceptionnelle ce mois : -8% sur l'achat " +
      "de tout véhicule neuf. Contactez-nous pour étudier votre flotte !\n\n" +
      "Cordialement,\n{senderName}",
  },
  {
    id: 'sup-recall-01', senderType: 'supplier', tone: 'urgent', kind: 'info',
    trigger: 'random', weight: 2, ttlDays: 3,
    subjectTemplate: 'Rappel constructeur — vérification',
    bodyTemplate:
      "Bonjour,\n\nNous procédons à un rappel sur un défaut potentiel du " +
      "système de freinage. Si vous avez un véhicule concerné, contactez-nous " +
      "pour planifier l'intervention (gratuite).\n\n{senderName}",
  },
  {
    id: 'sup-new-model-01', senderType: 'supplier', tone: 'friendly', kind: 'info',
    trigger: 'random', weight: 4, ttlDays: 14,
    subjectTemplate: 'Nouveau modèle disponible',
    bodyTemplate:
      "Notre nouveau {vehicleModel} arrive en concession. Économique, " +
      "fiable, parfait pour la longue distance. Démo possible sur RDV.\n\n" +
      "{senderName}",
  },
  {
    id: 'sup-parts-shortage-01', senderType: 'supplier', tone: 'urgent', kind: 'info',
    trigger: 'random', weight: 2, ttlDays: 5,
    subjectTemplate: 'Tension sur les pièces — délais rallongés',
    bodyTemplate:
      "Bonjour,\n\nSuite aux tensions sur la chaîne d'approvisionnement, " +
      "nous prévoyons des délais rallongés de 2 à 4 semaines sur les pièces " +
      "détachées. Anticipez vos commandes.\n\n{senderName}",
  },
  {
    id: 'sup-insurance-offer-01', senderType: 'supplier', tone: 'formal', kind: 'decision',
    trigger: 'random', weight: 3, ttlDays: 5, minDays: 30,
    subjectTemplate: 'Offre d\'assurance Premium',
    bodyTemplate:
      "Notre nouvelle formule Premium couvre désormais la cyber-fraude et " +
      "le vol de marchandises avec une franchise réduite. Souhaitez-vous " +
      "un devis personnalisé ?\n\n{senderName}",
    choices: [
      { id: 'quote', label: 'Demander un devis (sans engagement)', effects: [] },
      { id: 'skip',  label: 'Pas intéressé',                         effects: [] },
    ],
    defaultChoiceId: 'skip',
  },
  {
    id: 'sup-fuel-card-01', senderType: 'supplier', tone: 'friendly', kind: 'decision',
    trigger: 'random', weight: 3, ttlDays: 7, minDays: 60,
    subjectTemplate: 'Carte carburant pro — 6% remise',
    bodyTemplate:
      "Bonjour,\n\nProfitez d'une remise de 6% sur le diesel avec notre " +
      "carte carburant pro. Frais de gestion : 240 €/an.\n\n{senderName}",
    choices: [
      { id: 'sub',  label: 'Souscrire (240 €/an, économies attendues)', effects: [{ kind: 'treasury', amount: -240 }] },
      { id: 'pass', label: 'Décliner',                                    effects: [] },
    ],
    defaultChoiceId: 'pass',
  },

  // ───────────────────────────────────────────────────────────────────────────
  // BANQUE (15+ templates)
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'bank-cash-alert-01', senderType: 'bank', tone: 'urgent', kind: 'info',
    trigger: 'random', weight: 3, ttlDays: 3,
    // 02/06/2026 — Gate this alert on REAL treasury < 5 000 € to avoid the
    // false "comptes dans le rouge" message firing when treasury is fine.
    requires: { treasuryBelow: 5000 },
    subjectTemplate: 'Alerte trésorerie — solde proche du seuil',
    bodyTemplate:
      "Bonjour,\n\nVotre solde s'approche du seuil d'agios. Souhaitez-vous " +
      "que nous étudions un découvert autorisé temporaire ?\n\n" +
      "Cordialement,\nVotre conseiller bancaire",
  },
  {
    id: 'bank-credit-offer-01', senderType: 'bank', tone: 'formal', kind: 'decision',
    trigger: 'random', weight: 3, ttlDays: 7, minDays: 60,
    subjectTemplate: 'Pré-accord crédit — taux préférentiel',
    bodyTemplate:
      "Bonjour,\n\nAu vu de votre historique, nous pouvons vous proposer un " +
      "crédit pré-accordé de 25 000 € à un taux préférentiel de 3,2% sur " +
      "36 mois. Réponse souhaitée sous 7 jours.\n\nVotre conseiller bancaire",
    choices: [
      { id: 'accept', label: 'Accepter (25 000 € débloqués)', effects: [{ kind: 'creditOffer', amount: 25000, months: 36, rate: 0.032 }] },
      { id: 'later',  label: 'Reporter la décision',           effects: [] },
      { id: 'refuse', label: 'Refuser',                         effects: [] },
    ],
    defaultChoiceId: 'later',
  },
  {
    id: 'bank-credit-refused-01', senderType: 'bank', tone: 'formal', kind: 'info',
    trigger: 'random', weight: 2, ttlDays: 5,
    subjectTemplate: 'Refus de demande de crédit',
    bodyTemplate:
      "Bonjour,\n\nAprès étude de votre dossier, nous ne pouvons pas " +
      "donner suite à votre demande de financement. Votre score crédit ne " +
      "permet pas la prise de risque.\n\nVotre conseiller bancaire",
  },
  {
    id: 'bank-score-up-01', senderType: 'bank', tone: 'friendly', kind: 'info',
    trigger: 'random', weight: 3, ttlDays: 5,
    subjectTemplate: 'Bonne nouvelle — votre score crédit progresse',
    bodyTemplate:
      "Votre score crédit a progressé sur le dernier trimestre. Nous " +
      "pouvons désormais vous proposer des taux plus avantageux.\n\n" +
      "Votre conseiller bancaire",
  },
  {
    id: 'bank-meeting-01', senderType: 'bank', tone: 'formal', kind: 'decision',
    trigger: 'random', weight: 2, ttlDays: 5, minDays: 90,
    subjectTemplate: 'RDV annuel proposé',
    bodyTemplate:
      "Bonjour,\n\nVotre conseiller souhaite vous rencontrer pour faire un " +
      "point annuel sur votre situation financière. Acceptez-vous le RDV ?\n\n" +
      "Cordialement",
    choices: [
      { id: 'accept', label: 'Accepter (+5 score crédit)', effects: [{ kind: 'creditScore', amount: 5 }] },
      { id: 'decline', label: 'Décliner poliment',          effects: [] },
    ],
    defaultChoiceId: 'decline',
  },

  // ───────────────────────────────────────────────────────────────────────────
  // CONCURRENTS (15+ templates)
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'comp-provocation-01', senderType: 'competitor', tone: 'aggressive', kind: 'info',
    trigger: 'random', weight: 3, ttlDays: 5,
    subjectTemplate: 'Un mot d\'un confrère du marché',
    bodyTemplate:
      "Salut {companyName},\n\nOn voit que vous vous démenez. Bravo. " +
      "Mais le marché est petit. À un moment, il faudra choisir : grossir " +
      "ou disparaître. Bonne chance.\n\n{senderName} - {competitorName}",
  },
  {
    id: 'comp-poaching-01', senderType: 'competitor', tone: 'aggressive', kind: 'decision',
    trigger: 'random', weight: 2, ttlDays: 4, minDays: 90,
    subjectTemplate: 'Offre de rachat de votre meilleur chauffeur',
    bodyTemplate:
      "Bonjour,\n\nNous sommes prêts à recruter votre chauffeur " +
      "{driverName} avec un salaire +20%. Nous vous proposons une " +
      "indemnité de transfert de 1 500 € pour faciliter le départ.\n\n" +
      "Cordialement,\n{competitorName}",
    choices: [
      { id: 'sell',   label: 'Accepter les 1 500 € (perte chauffeur)', effects: [{ kind: 'treasury', amount: 1500 }, { kind: 'driverFire', amount: 0 }] },
      { id: 'counter', label: 'Augmenter mon chauffeur (+150 €)',       effects: [{ kind: 'driverRaise', amount: 150 }, { kind: 'driverSatisfaction', amount: 15 }] },
      { id: 'refuse', label: 'Ignorer (risque de démission)',           effects: [{ kind: 'driverSatisfaction', amount: -5 }] },
    ],
    defaultChoiceId: 'refuse',
  },
  {
    id: 'comp-alliance-01', senderType: 'competitor', tone: 'formal', kind: 'decision',
    trigger: 'random', weight: 1, ttlDays: 7, minDays: 180,
    subjectTemplate: 'Proposition de partenariat',
    bodyTemplate:
      "Bonjour,\n\nPlutôt que de nous concurrencer sur des contrats où " +
      "personne ne gagne, je propose une alliance ponctuelle : on partage " +
      "les pics de charge. Intéressé ?\n\n{senderName} - {competitorName}",
    choices: [
      { id: 'accept', label: 'Accepter (+2 notoriété, -1 réputation)', effects: [{ kind: 'notoriety', amount: 2 }, { kind: 'reputation', amount: -1 }] },
      { id: 'refuse', label: 'Refuser (jouer solo)',                    effects: [{ kind: 'notoriety', amount: 1 }] },
    ],
    defaultChoiceId: 'refuse',
  },
  {
    id: 'comp-rumor-01', senderType: 'competitor', tone: 'humour', kind: 'info',
    trigger: 'random', weight: 3, ttlDays: 4,
    subjectTemplate: 'Une rumeur circule',
    bodyTemplate:
      "Salut,\n\nOn raconte que {companyName} serait à vendre. Vrai ? " +
      "Si oui, ma porte est ouverte 😉\n\n{senderName} - {competitorName}",
  },
  {
    id: 'comp-respect-01', senderType: 'competitor', tone: 'formal', kind: 'info',
    trigger: 'random', weight: 1, ttlDays: 7, minDays: 240,
    subjectTemplate: 'Chapeau pour votre dernier coup',
    bodyTemplate:
      "Bonjour,\n\nOn a appris que vous aviez décroché le contrat dont tout " +
      "le monde parlait. Beau coup. À une prochaine sur le marché.\n\n" +
      "{senderName} - {competitorName}",
  },
  // 08/02/2026 — Q2 Phase 2 user prod : marché vivant. Les dirigeants
  // (Ivan, Geoffrey, Stéphane, Ruddy) commentent l'évolution du joueur
  // et du marché. Ces 4 templates utilisent {senderName} (dirigeant) +
  // {competitorName} (entreprise) tirés via le router senderType='competitor'.
  {
    id: 'comp-market-pulse-01', senderType: 'competitor', tone: 'neutral', kind: 'info',
    trigger: 'random', weight: 2, ttlDays: 6, minDays: 60,
    subjectTemplate: "Le marché bouge — analyse du semestre",
    bodyTemplate:
      "Bonjour,\n\nDans ma dernière interview au Logisticien, j'ai souligné " +
      "la consolidation rapide du secteur. Les nouveaux entrants comme " +
      "{companyName} vont devoir choisir : monter en gamme ou jouer le volume. " +
      "Pas de demi-mesure possible.\n\nCordialement,\n{senderName}\n— {competitorName}",
  },
  {
    id: 'comp-recruitment-leak-01', senderType: 'competitor', tone: 'humour', kind: 'info',
    trigger: 'random', weight: 1, ttlDays: 5, minDays: 120,
    subjectTemplate: "On parle de nos recrutements ?",
    bodyTemplate:
      "{senderName} ici. J'ai entendu dire que {companyName} cherchait à " +
      "débaucher chez nous. Je préfère qu'on en discute autour d'un café " +
      "plutôt que par cabinet RH interposé. Quoi qu'il en soit, le marché " +
      "des chauffeurs CE est tendu, ça nous coûte à tous les deux.\n\n" +
      "À très vite,\n{senderName} ({competitorName})",
  },
  {
    id: 'comp-public-call-01', senderType: 'competitor', tone: 'formal', kind: 'info',
    trigger: 'random', weight: 1, ttlDays: 7, minDays: 180,
    subjectTemplate: "Tribune Le Logisticien — réponse aux nouveaux entrants",
    bodyTemplate:
      "Madame, Monsieur,\n\n{senderName} a publié une tribune dans **Le " +
      "Logisticien** cette semaine. Extrait : « Nous saluons l'arrivée " +
      "d'acteurs comme {companyName}, mais le métier ne s'improvise pas. " +
      "Notre savoir-faire reste un cap difficile à franchir. »\n\nÀ méditer.\n\n" +
      "Service Communication — {competitorName}",
  },
  {
    id: 'comp-tender-boast-01', senderType: 'competitor', tone: 'aggressive', kind: 'info',
    trigger: 'random', weight: 2, ttlDays: 5, minDays: 90,
    subjectTemplate: "Bilan AO du mois — {competitorName} décroche encore",
    bodyTemplate:
      "Communiqué interne (fuite) :\n\n« Nous avons remporté **3 appels " +
      "d'offres** ce mois-ci, dont un face à {companyName}. {senderName} " +
      "a tenu à féliciter l'équipe commerciale. Notre stratégie de pricing " +
      "agressive porte ses fruits. »\n\nVu sur le compte LinkedIn de " +
      "{competitorName}.",
  },

  // ───────────────────────────────────────────────────────────────────────────
  // URSSAF / DOUANES (10+ templates)
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'urssaf-info-01', senderType: 'urssaf', tone: 'formal', kind: 'info',
    trigger: 'random', weight: 2, ttlDays: 7,
    subjectTemplate: 'Rappel — déclaration trimestrielle',
    bodyTemplate:
      "Madame, Monsieur,\n\nNous vous rappelons l'échéance de votre " +
      "déclaration trimestrielle dans 15 jours. Pensez à anticiper.\n\n" +
      "URSSAF Service Cotisations",
  },
  {
    id: 'urssaf-survey-01', senderType: 'urssaf', tone: 'formal', kind: 'decision',
    trigger: 'random', weight: 1, ttlDays: 5, minDays: 60,
    subjectTemplate: 'Enquête statistique',
    bodyTemplate:
      "Nous menons une enquête sur le secteur du transport. Votre " +
      "participation est facultative mais appréciée. Compter 20 minutes.\n\n" +
      "URSSAF Études",
    choices: [
      { id: 'fill', label: 'Répondre (+2 conformité)', effects: [{ kind: 'reputation', amount: 1 }] },
      { id: 'skip', label: 'Pas le temps',              effects: [] },
    ],
    defaultChoiceId: 'skip',
  },
  {
    id: 'douane-news-01', senderType: 'urssaf', tone: 'formal', kind: 'info',
    trigger: 'random', weight: 2, ttlDays: 6,
    subjectTemplate: 'Évolution réglementaire — Export EU',
    bodyTemplate:
      "Bonjour,\n\nÀ partir du mois prochain, le formulaire T1 sera " +
      "dématérialisé. Pensez à mettre à jour vos process si vous opérez " +
      "à l'international.\n\nDouanes & Droits Indirects",
  },

  // ───────────────────────────────────────────────────────────────────────────
  // MÉDIAS / PRESSE PRO (15+ templates)
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'media-interview-01', senderType: 'media', tone: 'friendly', kind: 'decision',
    trigger: 'random', weight: 2, ttlDays: 6, minDays: 90,
    subjectTemplate: 'Demande d\'interview — Logistique Magazine',
    bodyTemplate:
      "Bonjour,\n\nJe prépare un dossier sur les acteurs émergents du " +
      "transport régional. Acceptez-vous une interview téléphonique de " +
      "30 minutes ? Publication dans 2 mois.\n\nLa rédaction",
    choices: [
      { id: 'accept', label: 'Accepter (+4 notoriété)',  effects: [{ kind: 'notoriety', amount: 4 }] },
      { id: 'refuse', label: 'Décliner poliment',         effects: [] },
    ],
    defaultChoiceId: 'refuse',
  },
  {
    // 05/02/2026 (Q1 Lot 2) — Refonte salon marketing : ce message random
    // legacy reste pour compat. saves mais est rendu très rare (minDays 60→720,
    // weight 2→0). Le salon est désormais géré par `marketingSalonSlice` avec
    // un arc narratif dédié + budget tiers + résultats variables.
    id: 'media-salon-01', senderType: 'media', tone: 'formal', kind: 'decision',
    trigger: 'random', weight: 0, ttlDays: 7, minDays: 720,
    subjectTemplate: 'Invitation Salon Solutrans (legacy)',
    bodyTemplate:
      "Bonjour,\n\nVous êtes invité au Salon Solutrans (Lyon). Stand offert " +
      "si vous êtes exposant. Sinon, badge visiteur gratuit (250 € de valeur).\n\n" +
      "Organisation Salon",
    choices: [
      { id: 'exhibit', label: 'Exposer (-2 000 €, +6 notoriété)', effects: [{ kind: 'treasury', amount: -2000 }, { kind: 'notoriety', amount: 6 }] },
      { id: 'visit',   label: 'Visiter (gratuit, +1 notoriété)',  effects: [{ kind: 'notoriety', amount: 1 }] },
      { id: 'skip',    label: 'Ne pas y aller',                    effects: [] },
    ],
    defaultChoiceId: 'skip',
  },
  {
    id: 'media-award-01', senderType: 'media', tone: 'friendly', kind: 'info',
    trigger: 'random', weight: 1, ttlDays: 14, minDays: 360,
    subjectTemplate: 'Nomination — Prix Logistique Régional',
    bodyTemplate:
      "Félicitations ! Vous êtes nominé pour le Prix de la Logistique " +
      "Régionale dans la catégorie « PME en croissance ». Résultats au " +
      "salon dans 2 mois.\n\nOrganisation du Prix",
  },
  {
    id: 'media-article-01', senderType: 'media', tone: 'humour', kind: 'info',
    trigger: 'random', weight: 2, ttlDays: 5,
    subjectTemplate: '{companyName} cité dans Transports Actualités',
    bodyTemplate:
      "Bonjour,\n\nVous êtes mentionné(e) dans notre dernier numéro comme " +
      "« acteur à suivre ». Bonne lecture !\n\nLa rédaction",
  },
  {
    id: 'media-podcast-01', senderType: 'media', tone: 'friendly', kind: 'decision',
    trigger: 'random', weight: 1, ttlDays: 7, minDays: 180,
    subjectTemplate: 'Invitation podcast « Sur la route »',
    bodyTemplate:
      "Bonjour,\n\nNotre podcast cherche des entrepreneurs à interviewer. " +
      "30 min d'enregistrement, diffusion à 12 000 auditeurs.\n\nProd Podcast",
    choices: [
      { id: 'go',   label: 'Accepter (+5 notoriété)', effects: [{ kind: 'notoriety', amount: 5 }] },
      { id: 'pass', label: 'Passer',                   effects: [] },
    ],
    defaultChoiceId: 'pass',
  },

  // ───────────────────────────────────────────────────────────────────────────
  // INSPECTEUR DU TRAVAIL (8+ templates)
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'insp-visit-01', senderType: 'inspector', tone: 'formal', kind: 'info',
    trigger: 'random', weight: 1, ttlDays: 5,
    subjectTemplate: 'Visite annoncée — Inspection du Travail',
    bodyTemplate:
      "Madame, Monsieur,\n\nUne visite de routine est prévue dans 10 jours. " +
      "Préparez les documents : contrats, temps de service, équipements " +
      "de sécurité.\n\nInspection du Travail",
  },
  {
    id: 'insp-form-01', senderType: 'inspector', tone: 'formal', kind: 'decision',
    trigger: 'random', weight: 1, ttlDays: 7, minDays: 30,
    subjectTemplate: 'Formulaire — données sociales annuelles',
    bodyTemplate:
      "Bonjour,\n\nMerci de retourner le formulaire DSA avant 15 jours. " +
      "Un retard expose à des pénalités.\n\nInspection du Travail",
    choices: [
      { id: 'send', label: 'Envoyer (conformité +)',          effects: [{ kind: 'reputation', amount: 1 }] },
      { id: 'wait', label: 'Faire à la dernière minute',      effects: [] },
      { id: 'skip', label: 'Ignorer (risque pénalité)',       effects: [{ kind: 'treasury', amount: -500 }, { kind: 'reputation', amount: -3 }] },
    ],
    defaultChoiceId: 'send',
  },

  // ───────────────────────────────────────────────────────────────────────────
  // HR INTERNE / RAPPORTS (10+ templates)
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'hr-recap-01', senderType: 'hr', tone: 'neutral', kind: 'info',
    // 02/06/2026 — Gate sur taille d'équipe ≥ 4 (sinon ce "point équipe"
    // fictif tombait alors qu'il n'y avait que 1-2 chauffeurs). Texte
    // dégénérique (plus de "67%" inventé).
    trigger: 'random', weight: 2, ttlDays: 14,
    requires: { driverCountMin: 4 },
    subjectTemplate: 'Récap mensuel — équipe',
    bodyTemplate:
      "Bonjour,\n\nLe ressenti de l'équipe ce mois est globalement stable. " +
      "Quelques demandes de formation à étudier et un point équipe trimestriel " +
      "serait apprécié pour entretenir la cohésion.\n\nVotre RH",
  },
  {
    id: 'hr-survey-01', senderType: 'hr', tone: 'formal', kind: 'info',
    trigger: 'random', weight: 2, ttlDays: 7, minDays: 180,
    subjectTemplate: 'Enquête climat social — résultats',
    bodyTemplate:
      "Bonjour,\n\nL'enquête trimestrielle révèle : ambiance correcte, " +
      "frustrations sur les délais, demande de plus de reconnaissance. " +
      "Un plan d'action serait apprécié.\n\nVotre RH",
  },

  // ───────────────────────────────────────────────────────────────────────────
  // NEWS / ACTUALITÉS SECTEUR (10+ templates)
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'news-fuel-up-01', senderType: 'news', tone: 'urgent', kind: 'info',
    trigger: 'random', weight: 3, ttlDays: 4,
    subjectTemplate: 'Hausse du diesel à la pompe (+5%)',
    bodyTemplate:
      "Les prix du gazole repartent à la hausse cette semaine. Les acteurs " +
      "du transport pointent l'impact sur leurs marges. Voir l'article complet.\n\n" +
      "Impact : +5 % sur votre conso carburant pendant 21 jours.\n\n" +
      "Actu Transport",
    // 06/02/2026 — P2 : effet réel appliqué automatiquement à réception.
    autoEffects: [{ kind: 'fuelPriceShock', multiplier: 1.05, durationDays: 21, source: 'fuel-up' }],
  },
  // 10/02/2026 (Lot J P1) — Retour user prod : « les chocs sont rares mais
  // oui sur une période par exemple conflits internationaux ». Trois
  // événements majeurs et rares (weight 1, minDays 90+) qui modélisent
  // des chocs structurels sur le cours du carburant.
  {
    id: 'news-geopol-conflict-01', senderType: 'news', tone: 'urgent', kind: 'info',
    trigger: 'random', weight: 1, ttlDays: 5, minDays: 120,
    subjectTemplate: '⚠ Conflit international — flambée du diesel (+40 %)',
    bodyTemplate:
      "Tensions géopolitiques majeures sur le golfe Persique. Les marchés " +
      "anticipent une rupture d'approvisionnement et le baril s'envole.\n\n" +
      "Impact : +40 % sur votre conso carburant pendant 45 jours. Anticipez " +
      "les coûts ou consultez Rémi Forni (Resp. exploitation) pour optimiser " +
      "les tournées.\n\n" +
      "Actu Transport",
    autoEffects: [{ kind: 'fuelPriceShock', multiplier: 1.40, durationDays: 45, source: 'fuel-conflict' }],
  },
  {
    id: 'news-opep-tensions-01', senderType: 'news', tone: 'urgent', kind: 'info',
    trigger: 'random', weight: 1, ttlDays: 5, minDays: 90,
    subjectTemplate: 'OPEP — Coupe surprise de la production (+25 %)',
    bodyTemplate:
      "L'OPEP+ annonce une coupe surprise de 2 millions de barils/jour. Les " +
      "raffineurs européens répercutent en quelques semaines.\n\n" +
      "Impact : +25 % sur votre conso carburant pendant 35 jours.\n\n" +
      "Actu Transport",
    autoEffects: [{ kind: 'fuelPriceShock', multiplier: 1.25, durationDays: 35, source: 'fuel-opep' }],
  },
  {
    id: 'news-fuel-crash-01', senderType: 'news', tone: 'friendly', kind: 'info',
    trigger: 'random', weight: 1, ttlDays: 6, minDays: 90,
    subjectTemplate: 'Effondrement du diesel — récession mondiale (-20 %)',
    bodyTemplate:
      "Ralentissement économique mondial : la demande de fret international " +
      "s'effondre, le baril perd 30 %. Bonne nouvelle ponctuelle pour vos " +
      "marges, mais signal de prudence sur la demande.\n\n" +
      "Impact : -20 % sur votre conso carburant pendant 30 jours.\n\n" +
      "Actu Transport",
    autoEffects: [{ kind: 'fuelPriceShock', multiplier: 0.80, durationDays: 30, source: 'fuel-crash' }],
  },
  {
    id: 'news-strike-01', senderType: 'news', tone: 'urgent', kind: 'info',
    trigger: 'random', weight: 2, ttlDays: 5,
    subjectTemplate: 'Grève annoncée — péages bloqués',
    bodyTemplate:
      "Une grève des chauffeurs routiers est annoncée pour la semaine " +
      "prochaine. Plusieurs péages risquent d'être bloqués sur l'A7.\n\n" +
      "Impact : +180 € / camion en mission / jour pendant 5 jours " +
      "(détour par réseau secondaire, péages alternatifs).\n\n" +
      "Actu Transport",
    autoEffects: [{ kind: 'peageStrike', dailyCostPerVehicle: 180, durationDays: 5, source: 'strike' }],
  },
  {
    id: 'news-regulation-01', senderType: 'news', tone: 'neutral', kind: 'info',
    trigger: 'random', weight: 2, ttlDays: 7,
    subjectTemplate: 'Nouvelle norme Euro 7 — délais',
    bodyTemplate:
      "L'Union européenne a publié les modalités de la norme Euro 7. " +
      "Les véhicules neufs devront s'y conformer d'ici 2027. Les flottes " +
      "récentes sont protégées.\n\nUE Transports",
  },
  {
    id: 'news-eco-01', senderType: 'news', tone: 'friendly', kind: 'info',
    trigger: 'random', weight: 2, ttlDays: 6,
    subjectTemplate: 'Aide gouvernementale — flotte électrique',
    bodyTemplate:
      "Le gouvernement étend les primes de conversion aux poids lourds " +
      "électriques (10 000 € par véhicule). Vous pourriez en bénéficier.\n\n" +
      "Actu Transport",
  },

  // ───────────────────────────────────────────────────────────────────────────
  // ÉVÉNEMENTS LIÉS (déclenchés par triggers spécifiques — pas random)
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'evt-retirement-msg', senderType: 'driver', tone: 'formal', kind: 'decision',
    trigger: 'event:evt-hr-retirement', ttlDays: 7,
    subjectTemplate: 'Départ à la retraite — {driverName}',
    bodyTemplate:
      "Bonjour,\n\nJ'ai longuement réfléchi et le moment est venu pour moi " +
      "de partir à la retraite. Je souhaite quitter {companyName} d'ici " +
      "un mois. Comment souhaitez-vous organiser la transition ?\n\n" +
      "{driverName}",
    choices: [
      { id: 'celebrate', label: 'Pot de départ + cadeau (500 €)', effects: [{ kind: 'treasury', amount: -500 }, { kind: 'reputation', amount: 3 }] },
      { id: 'replace',   label: 'Lancer un recrutement immédiat', effects: [{ kind: 'notoriety', amount: 1 }] },
      { id: 'ignore',    label: 'Ne rien faire (départ silencieux)', effects: [{ kind: 'reputation', amount: -2 }] },
    ],
    defaultChoiceId: 'replace',
  },
  {
    id: 'evt-accident-msg', senderType: 'driver', tone: 'urgent', kind: 'decision',
    trigger: 'event:evt-acc-01', ttlDays: 2,
    subjectTemplate: 'Accident — décision urgente',
    bodyTemplate:
      "Patron, j'ai eu un accident léger ce matin. Le camion est cabossé " +
      "mais roulable. Aucun blessé. Que faisons-nous ?\n\n{driverName}",
    choices: [
      { id: 'repair', label: 'Réparation immédiate (-1500 €)', effects: [{ kind: 'treasury', amount: -1500 }] },
      { id: 'wait',   label: 'Attendre la fin du contrat',      effects: [{ kind: 'reputation', amount: -2 }] },
      { id: 'claim',  label: 'Faire jouer l\'assurance (franchise 500 €)', effects: [{ kind: 'treasury', amount: -500 }] },
    ],
    defaultChoiceId: 'claim',
  },

  // ───────────────────────────────────────────────────────────────────────────
  // SYSTÈME / NARRATIF (12+ templates)
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'sys-welcome-01', senderType: 'system', tone: 'neutral', kind: 'info',
    trigger: 'milestone:companyCreated', ttlDays: 999,
    subjectTemplate: 'Bienvenue chez {companyName}',
    bodyTemplate:
      "Bonjour {playerName},\n\nVotre messagerie est désormais active. Vous " +
      "y recevrez les messages de vos collaborateurs, clients, partenaires " +
      "bancaires et concurrents. Certains messages requièrent une décision " +
      "(répondez avant la date d'expiration), d'autres sont purement " +
      "informatifs.\n\nBonne route !\n\nL'équipe HST",
  },
  {
    id: 'sys-milestone-100k-01', senderType: 'system', tone: 'friendly', kind: 'info',
    trigger: 'milestone:100k_revenue', ttlDays: 30,
    subjectTemplate: '🎉 100 000 € de chiffre d\'affaires !',
    bodyTemplate:
      "Bravo, vous venez de franchir les 100 000 € de chiffre d'affaires " +
      "cumulé. Continuez sur cette lancée !\n\nL'équipe HST",
  },
  {
    id: 'sys-milestone-firstyear-01', senderType: 'system', tone: 'friendly', kind: 'info',
    trigger: 'milestone:firstYear', ttlDays: 30,
    subjectTemplate: '🎂 1 an d\'activité',
    bodyTemplate:
      "Un an que vous pilotez {companyName} ! Un bilan annuel sera bientôt " +
      "présenté en Conseil d'administration. Préparez-vous.\n\nL'équipe HST",
  },

  // ═════════════════════════════════════════════════════════════════════════
  // BATCH 2 — Enrichissement narratif (40+ templates)
  // ═════════════════════════════════════════════════════════════════════════

  // ── Anecdotes humoristiques chauffeurs ───────────────────────────────────
  {
    id: 'drv-cafe-01', senderType: 'driver', tone: 'humour', kind: 'info',
    trigger: 'random', weight: 4, ttlDays: 4,
    subjectTemplate: 'Le café de l\'aire de service ☕',
    bodyTemplate:
      "Salut chef,\n\nL'aire de Mâcon-Sud a enfin changé sa machine à café. " +
      "C'est plus du jus de chaussette ! Un détail, mais ça change la journée 😄\n\n{driverFirstName}",
  },
  {
    id: 'drv-cargo-strange-01', senderType: 'driver', tone: 'humour', kind: 'info',
    trigger: 'random', weight: 3, ttlDays: 4,
    subjectTemplate: 'Curieux chargement aujourd\'hui',
    bodyTemplate:
      "Tu vas pas me croire — j'ai transporté 200 mètres carrés de fausse " +
      "neige pour un tournage. Les chargeurs riaient. Bref, journée folklorique.\n\n{driverFirstName}",
  },
  {
    id: 'drv-traffic-01', senderType: 'driver', tone: 'humour', kind: 'info',
    trigger: 'random', weight: 4, ttlDays: 3,
    subjectTemplate: 'Bouchon homérique sur A4',
    bodyTemplate:
      "3h pour faire 12 km. J'ai eu le temps de regarder un épisode entier " +
      "de podcast et de discuter avec mon voisin de camion. On a échangé " +
      "des conseils de chef.\n\n{driverFirstName}",
  },
  {
    id: 'drv-fan-mail-01', senderType: 'driver', tone: 'humour', kind: 'info',
    trigger: 'random', weight: 2, ttlDays: 5,
    subjectTemplate: 'Un enfant a pris notre camion en photo',
    bodyTemplate:
      "Au péage de Beaune, un gamin de 6 ans m'a fait des grands gestes et " +
      "voulu absolument prendre notre camion en photo. La maman m'a dit qu'il " +
      "voulait être chauffeur. Du coup, je me sens un peu modèle aujourd'hui 🥹\n\n{driverFirstName}",
  },
  {
    id: 'drv-hitchhiker-01', senderType: 'driver', tone: 'humour', kind: 'decision',
    trigger: 'random', weight: 2, ttlDays: 2,
    subjectTemplate: 'Un auto-stoppeur me supplie 😬',
    bodyTemplate:
      "Je suis à l'aire de Vienne-Sud, un type me demande de le déposer à " +
      "Lyon. Il a l'air sympa mais la consigne c'est zéro auto-stop. Je fais quoi ?\n\n{driverFirstName}",
    choices: [
      { id: 'rules',  label: 'Refuser (consigne respectée)',       effects: [{ kind: 'driverSatisfaction', amount: 1 }, { kind: 'reputation', amount: 1 }] },
      { id: 'bend',   label: 'L\'accepter quand même',              effects: [{ kind: 'driverSatisfaction', amount: 3 }, { kind: 'reputation', amount: -3 }] },
    ],
    defaultChoiceId: 'rules',
  },
  {
    id: 'drv-radio-01', senderType: 'driver', tone: 'humour', kind: 'info',
    trigger: 'random', weight: 3, ttlDays: 4,
    subjectTemplate: 'Top 50 des années 80 sur RVR',
    bodyTemplate:
      "Je suis tombé sur une émission spéciale variété 80s ce matin. J'ai " +
      "chanté Goldman à tue-tête pendant 200 km. Ne le répétez pas.\n\n{driverFirstName}",
  },
  {
    id: 'drv-rain-01', senderType: 'driver', tone: 'humour', kind: 'info',
    trigger: 'random', weight: 3, ttlDays: 3,
    subjectTemplate: 'Le ciel m\'en a voulu aujourd\'hui ☔',
    bodyTemplate:
      "Trois orages, deux carrefours inondés, un essuie-glace qui rend l'âme. " +
      "Sinon ça va. Demain ça ira mieux. 🌦\n\n{driverFirstName}",
  },

  // ── Conflits internes & ambiance équipe ─────────────────────────────────
  {
    id: 'drv-parking-conflict-01', senderType: 'driver', tone: 'aggressive', kind: 'decision',
    trigger: 'random', weight: 3, ttlDays: 4, minDays: 40,
    subjectTemplate: 'Place de parking — tensions au dépôt',
    bodyTemplate:
      "Patron,\n\nUn collègue squatte systématiquement la place ombragée " +
      "près du quai. Plusieurs se sont plaints. Faut un peu d'ordre.\n\n{driverName}",
    choices: [
      { id: 'rotate', label: 'Imposer une rotation hebdo',  effects: [{ kind: 'driverSatisfaction', amount: 4 }] },
      { id: 'talk',   label: 'En parler discrètement',       effects: [{ kind: 'driverSatisfaction', amount: 1 }] },
      { id: 'ignore', label: 'Laisser tomber',               effects: [{ kind: 'driverSatisfaction', amount: -4 }] },
    ],
    defaultChoiceId: 'talk',
  },
  {
    id: 'drv-night-shift-01', senderType: 'driver', tone: 'formal', kind: 'decision',
    trigger: 'random', weight: 3, ttlDays: 5, minDays: 60,
    subjectTemplate: 'Volontariat pour les nuits',
    bodyTemplate:
      "Bonjour,\n\nJe suis disponible pour prendre des tournées de nuit " +
      "(meilleure prime). Mais je veux une visibilité sur les 3 prochaines " +
      "semaines.\n\n{driverName}",
    choices: [
      { id: 'plan',   label: 'Lui établir un planning nuit',  effects: [{ kind: 'driverSatisfaction', amount: 6 }, { kind: 'driverRaise', amount: 80 }] },
      { id: 'ad-hoc', label: 'Au cas par cas',                 effects: [{ kind: 'driverSatisfaction', amount: 2 }] },
      { id: 'no',     label: 'Pas de nuits cette période',     effects: [{ kind: 'driverSatisfaction', amount: -2 }] },
    ],
    defaultChoiceId: 'ad-hoc',
  },
  {
    id: 'drv-incident-other-01', senderType: 'driver', tone: 'urgent', kind: 'info',
    trigger: 'random', weight: 2, ttlDays: 2,
    subjectTemplate: 'Bagarre à l\'entrepôt clientèle',
    bodyTemplate:
      "Patron, j'ai assisté à un échange musclé entre un manutentionnaire " +
      "client et un autre chauffeur (pas chez nous). Tout s'est calmé. Je " +
      "préfère vous prévenir.\n\n{driverFirstName}",
  },
  {
    id: 'drv-buddy-01', senderType: 'driver', tone: 'friendly', kind: 'info',
    trigger: 'random', weight: 3, ttlDays: 5,
    subjectTemplate: 'Belle ambiance hier soir',
    bodyTemplate:
      "Salut,\n\nOn s'est retrouvés à quelques uns au resto du coin après la " +
      "tournée. Bonne soirée, ça soude l'équipe. Faudrait penser à un repas " +
      "d'entreprise un de ces jours.\n\n{driverFirstName}",
  },
  {
    id: 'drv-mentor-01', senderType: 'driver', tone: 'friendly', kind: 'decision',
    trigger: 'random', weight: 2, ttlDays: 6, minDays: 180,
    subjectTemplate: 'Devenir tuteur pour les nouveaux ?',
    bodyTemplate:
      "Bonjour,\n\nJ'ai constaté qu'on n'avait pas vraiment de système " +
      "d'intégration. J'aimerais bien aider à former les nouveaux. Une " +
      "prime symbolique encourageraît la démarche.\n\n{driverName}",
    choices: [
      { id: 'yes',    label: 'Mettre en place le programme (+50 €/mois)', effects: [{ kind: 'driverRaise', amount: 50 }, { kind: 'reputation', amount: 2 }] },
      { id: 'thanks', label: 'Le remercier sans formaliser',               effects: [{ kind: 'driverSatisfaction', amount: 4 }] },
      { id: 'no',     label: 'Pas le moment',                              effects: [{ kind: 'driverSatisfaction', amount: -2 }] },
    ],
    defaultChoiceId: 'thanks',
  },
  {
    id: 'drv-snitch-01', senderType: 'driver', tone: 'aggressive', kind: 'decision',
    trigger: 'random', weight: 2, ttlDays: 3, minDays: 90,
    subjectTemplate: 'Signalement confidentiel',
    bodyTemplate:
      "Bonjour,\n\nJe préfère vous prévenir : un de mes collègues prend des " +
      "libertés avec les temps de pause. Si ça se sait à l'inspection, ça " +
      "peut nous coûter.\n\n{driverName}",
    choices: [
      { id: 'investigate', label: 'Enquêter discrètement',          effects: [{ kind: 'reputation', amount: 2 }] },
      { id: 'sermon',      label: 'Rappel général aux règles',       effects: [{ kind: 'driverSatisfaction', amount: -2 }] },
      { id: 'ignore',      label: 'Ne rien faire',                    effects: [{ kind: 'reputation', amount: -3 }] },
    ],
    defaultChoiceId: 'sermon',
  },

  // ── Opérations marketing & croissance ───────────────────────────────────
  {
    id: 'mkt-sponsoring-01', senderType: 'system', tone: 'formal', kind: 'decision',
    trigger: 'random', weight: 2, ttlDays: 6, minDays: 120,
    subjectTemplate: 'Sponsoring club sportif local',
    bodyTemplate:
      "Le club de rugby de {city} cherche un sponsor pour la saison. " +
      "1 200 € pour figurer sur les maillots — visibilité locale assurée.\n\n" +
      "Club RC {city}",
    choices: [
      { id: 'sponsor', label: 'Sponsoriser (1 200 € / saison)', effects: [{ kind: 'treasury', amount: -1200 }, { kind: 'notoriety', amount: 6 }] },
      { id: 'half',    label: 'Sponsoring partiel (600 €)',      effects: [{ kind: 'treasury', amount: -600  }, { kind: 'notoriety', amount: 2 }] },
      { id: 'no',      label: 'Décliner',                         effects: [] },
    ],
    defaultChoiceId: 'no',
  },
  {
    id: 'mkt-flyer-01', senderType: 'supplier', tone: 'friendly', kind: 'decision',
    trigger: 'random', weight: 2, ttlDays: 5,
    subjectTemplate: 'Distribution de flyers — zone industrielle',
    bodyTemplate:
      "Bonjour,\n\nNotre agence propose un dépôt de 2 000 flyers dans toutes " +
      "les boîtes aux lettres de la ZI voisine. Coût : 480 €.\n\nMK Plus",
    choices: [
      { id: 'go',   label: 'Lancer la campagne',  effects: [{ kind: 'treasury', amount: -480 }, { kind: 'notoriety', amount: 3 }] },
      { id: 'pass', label: 'Pas le moment',        effects: [] },
    ],
    defaultChoiceId: 'pass',
  },
  {
    id: 'mkt-website-01', senderType: 'supplier', tone: 'formal', kind: 'info',
    trigger: 'random', weight: 3, ttlDays: 7,
    subjectTemplate: 'Refonte de votre site web ?',
    bodyTemplate:
      "Bonjour,\n\nNous proposons des refontes de sites pour PME du transport. " +
      "Un site moderne renforce la confiance des donneurs d'ordre. Devis " +
      "gratuit.\n\nWebPro France",
  },
  {
    id: 'mkt-school-visit-01', senderType: 'media', tone: 'friendly', kind: 'decision',
    trigger: 'random', weight: 2, ttlDays: 7, minDays: 60,
    subjectTemplate: 'Visite d\'élèves au dépôt',
    bodyTemplate:
      "Bonjour,\n\nDes élèves de CFA Transport souhaitent visiter votre " +
      "dépôt (groupe de 12, demi-journée). Excellent pour votre image et " +
      "votre vivier de recrutement.\n\nCFA Transport {city}",
    choices: [
      { id: 'yes',    label: 'Accueillir (matin libre, +3 réputation)', effects: [{ kind: 'reputation', amount: 3 }, { kind: 'notoriety', amount: 2 }] },
      { id: 'later',  label: 'Plus tard',                                 effects: [] },
      { id: 'no',     label: 'Décliner',                                   effects: [{ kind: 'reputation', amount: -1 }] },
    ],
    defaultChoiceId: 'later',
  },
  {
    id: 'mkt-charity-01', senderType: 'media', tone: 'friendly', kind: 'decision',
    trigger: 'random', weight: 1, ttlDays: 7, minDays: 120,
    subjectTemplate: 'Opération transport solidaire',
    bodyTemplate:
      "Une association demande un véhicule + chauffeur bénévoles pour " +
      "transporter du matériel humanitaire (1 journée). Engagement non " +
      "commercial.\n\nAsso TransportSolidaire",
    choices: [
      { id: 'help', label: 'Participer (1 jour off + carburant)', effects: [{ kind: 'reputation', amount: 6 }, { kind: 'notoriety', amount: 3 }, { kind: 'treasury', amount: -200 }] },
      { id: 'no',   label: 'Décliner',                              effects: [] },
    ],
    defaultChoiceId: 'no',
  },
  {
    id: 'mkt-trade-up-01', senderType: 'supplier', tone: 'formal', kind: 'decision',
    trigger: 'random', weight: 2, ttlDays: 5, minDays: 180,
    subjectTemplate: 'Reprise + neuf — offre exceptionnelle',
    bodyTemplate:
      "Bonjour,\n\nNous reprenons votre véhicule le plus ancien au prix " +
      "argus +10% si vous nous commandez un neuf. Offre valable 5 jours.\n\n" +
      "Concession AutoFleet",
    choices: [
      { id: 'study', label: 'Étudier la proposition', effects: [] },
      { id: 'pass',  label: 'Pas intéressé',           effects: [] },
    ],
    defaultChoiceId: 'pass',
  },

  // ── Rivalités locales & politique ───────────────────────────────────────
  {
    id: 'comp-pricewar-01', senderType: 'competitor', tone: 'aggressive', kind: 'info',
    trigger: 'random', weight: 2, ttlDays: 5, minDays: 90,
    subjectTemplate: 'Guerre des prix sur le bassin',
    bodyTemplate:
      "Salut,\n\nJ'ai cassé mes tarifs de 12% sur la zone {city}. Vos clients " +
      "vont commencer à venir voir. À toi de jouer.\n\n{senderName} - {competitorName}",
  },
  {
    id: 'comp-driver-leak-01', senderType: 'competitor', tone: 'humour', kind: 'info',
    trigger: 'random', weight: 2, ttlDays: 4,
    subjectTemplate: 'Vos chauffeurs parlent dans les aires',
    bodyTemplate:
      "Petit conseil entre nous : un de vos chauffeurs n'arrête pas de se " +
      "plaindre dans les aires de service. C'est mauvais pour votre image. " +
      "Faudrait remettre du ciment.\n\n{senderName} - {competitorName}",
  },
  {
    id: 'comp-merger-01', senderType: 'competitor', tone: 'formal', kind: 'decision',
    trigger: 'random', weight: 1, ttlDays: 10, minDays: 360,
    subjectTemplate: 'Proposition de rapprochement',
    bodyTemplate:
      "Bonjour,\n\nNos deux PME ont des forces complémentaires. Et si on " +
      "discutait d'un rapprochement (joint-venture, partage de moyens) ? " +
      "Sans engagement.\n\n{senderName} - {competitorName}",
    choices: [
      { id: 'meet',  label: 'Accepter un RDV exploratoire',     effects: [{ kind: 'notoriety', amount: 3 }] },
      { id: 'later', label: 'Reporter à plus tard',              effects: [] },
      { id: 'no',    label: 'Refuser fermement',                  effects: [{ kind: 'notoriety', amount: -1 }] },
    ],
    defaultChoiceId: 'later',
  },
  {
    id: 'comp-poach-attempt-01', senderType: 'competitor', tone: 'aggressive', kind: 'info',
    trigger: 'random', weight: 3, ttlDays: 4, minDays: 60,
    subjectTemplate: 'On vous prend votre meilleur client ?',
    bodyTemplate:
      "On a décroché un de vos contrats juteux. Ne le prenez pas mal — c'est " +
      "le marché. Faudra suivre.\n\n{senderName} - {competitorName}",
  },

  // ── Banque enrichie ─────────────────────────────────────────────────────
  {
    id: 'bank-product-01', senderType: 'bank', tone: 'formal', kind: 'decision',
    trigger: 'random', weight: 2, ttlDays: 6, minDays: 90,
    subjectTemplate: 'Compte placement entreprise',
    bodyTemplate:
      "Bonjour,\n\nVotre trésorerie dort. Notre placement professionnel " +
      "rémunère 2,5 % brut. Bloqué 12 mois. Souhaitez-vous y placer " +
      "10 000 € ?\n\nVotre conseiller bancaire",
    choices: [
      // 15/06/2026 — fix : utilisait un simple débit treasury sans créer le
      // placement. Le kind 'placement' crée l'entrée visible dans Banque →
      // Placements (2,5 %, bloqué 12 mois) et ne débite que si les fonds
      // sont disponibles.
      { id: 'place', label: 'Placer 10 000 € (bloqués 12 mois)', effects: [{ kind: 'placement', amount: 10000, ratePct: 2.5, lockMonths: 12, name: 'Compte placement entreprise' }, { kind: 'creditScore', amount: 3 }] },
      { id: 'no',    label: 'Préférer la liquidité',              effects: [] },
    ],
    defaultChoiceId: 'no',
  },
  {
    id: 'bank-fees-01', senderType: 'bank', tone: 'urgent', kind: 'info',
    trigger: 'random', weight: 3, ttlDays: 3,
    subjectTemplate: 'Frais bancaires en hausse',
    bodyTemplate:
      "Suite à un changement de grille tarifaire, vos frais mensuels " +
      "augmenteront de 12 € à partir du mois prochain. Pensez à comparer.\n\n" +
      "Votre conseiller bancaire",
  },
  {
    id: 'bank-app-01', senderType: 'bank', tone: 'friendly', kind: 'info',
    trigger: 'random', weight: 2, ttlDays: 5,
    subjectTemplate: 'Nouvelle app pro — gain de temps',
    bodyTemplate:
      "Bonne nouvelle : notre nouvelle application pro intègre la signature " +
      "électronique de chèques et l'export comptable. Téléchargeable " +
      "gratuitement.\n\nVotre conseiller bancaire",
  },

  // ── Client enrichi ──────────────────────────────────────────────────────
  {
    id: 'cli-gift-01', senderType: 'client', tone: 'friendly', kind: 'info',
    trigger: 'random', weight: 2, ttlDays: 5, minDays: 180,
    subjectTemplate: 'Petit cadeau de fin d\'année 🎁',
    bodyTemplate:
      "Bonjour,\n\nMerci pour votre travail sur nos livraisons cette année. " +
      "Vous trouverez un colis-cadeau à votre attention dans votre prochaine " +
      "livraison.\n\n{clientName}",
  },
  {
    id: 'cli-recommendation-01', senderType: 'client', tone: 'friendly', kind: 'info',
    trigger: 'random', weight: 3, ttlDays: 5,
    subjectTemplate: 'Je vous ai recommandé à un partenaire',
    bodyTemplate:
      "Bonjour,\n\nJ'ai parlé de {companyName} à un de nos partenaires qui " +
      "cherche un transporteur sérieux. Ne soyez pas surpris s'ils vous " +
      "contactent.\n\n{clientName}",
  },
  {
    id: 'cli-feedback-survey-01', senderType: 'client', tone: 'formal', kind: 'decision',
    trigger: 'random', weight: 2, ttlDays: 5,
    subjectTemplate: 'Demande d\'enquête qualité',
    bodyTemplate:
      "Bonjour,\n\nNous lançons une revue de nos prestataires logistiques. " +
      "Voulez-vous remplir notre questionnaire qualité (15 questions) ?\n\n" +
      "Service Achats — {clientName}",
    choices: [
      { id: 'fill', label: 'Le remplir consciencieusement',      effects: [{ kind: 'reputation', amount: 2 }] },
      { id: 'late', label: 'Le remplir au dernier moment',        effects: [{ kind: 'reputation', amount: 1 }] },
      { id: 'skip', label: 'Pas le temps',                         effects: [{ kind: 'reputation', amount: -2 }] },
    ],
    defaultChoiceId: 'late',
  },
  {
    id: 'cli-eco-pressure-01', senderType: 'client', tone: 'formal', kind: 'decision',
    trigger: 'random', weight: 2, ttlDays: 6, minDays: 240,
    subjectTemplate: 'Bilan carbone exigé',
    bodyTemplate:
      "Bonjour,\n\nNous exigeons désormais un bilan carbone détaillé de nos " +
      "transporteurs. Sans ce document, nous ne pourrons pas renouveler.\n\n" +
      "Service RSE — {clientName}",
    choices: [
      { id: 'commit', label: 'Lancer un bilan carbone (1 500 €)',  effects: [{ kind: 'treasury', amount: -1500 }, { kind: 'reputation', amount: 4 }] },
      { id: 'plan',   label: 'Promettre pour le trimestre suivant', effects: [{ kind: 'reputation', amount: 1 }] },
      { id: 'no',     label: 'Refuser (perte du contrat)',           effects: [{ kind: 'reputation', amount: -5 }] },
    ],
    defaultChoiceId: 'plan',
  },

  // ── Tier-driven client templates (option P1 — narrative relationship system).
  // Each template carries a `tierFilter` array : it only fires when AT LEAST
  // ONE recurring client is currently in one of those tiers. The chosen
  // {clientName} placeholder is bound to a real recurring client picked from
  // that tier — never a random activeContract. See messageSlice.tickMessages
  // for the picker logic (added 27/05/2026).

  // ── Tier MEFIANT (complaints, threats) ─────────────────────────────────
  {
    id: 'cli-mefiant-complaint-01', senderType: 'client', tone: 'aggressive', kind: 'decision',
    trigger: 'random', weight: 3, ttlDays: 4,
    tierFilter: ['mefiant'],
    subjectTemplate: 'Service défaillant — mise en demeure',
    bodyTemplate:
      "Bonjour,\n\nNotre dernière prestation a, encore une fois, été sous le " +
      "niveau acceptable. Nous formalisons par la présente une mise en " +
      "demeure : redressez la situation dans le mois ou nous résilierons " +
      "le contrat.\n\nService Achats — {clientName}",
    choices: [
      { id: 'apologise', label: 'Excuses + plan d\'action chiffré (300 €)', effects: [{ kind: 'treasury', amount: -300 }, { kind: 'reputation', amount: 2 }] },
      { id: 'meet',      label: 'Demander un RDV de crise', effects: [{ kind: 'reputation', amount: 1 }] },
      { id: 'ignore',    label: 'Ne pas répondre',           effects: [{ kind: 'reputation', amount: -3 }] },
    ],
    defaultChoiceId: 'meet',
  },
  {
    id: 'cli-mefiant-threat-01', senderType: 'client', tone: 'aggressive', kind: 'decision',
    trigger: 'random', weight: 2, ttlDays: 3,
    tierFilter: ['mefiant'],
    subjectTemplate: 'Nous consultons d\'autres transporteurs',
    bodyTemplate:
      "Bonjour,\n\nNous lançons une consultation auprès de 3 autres " +
      "transporteurs. Vous êtes consulté·e à titre indicatif mais " +
      "honnêtement, votre dossier est mal positionné.\n\n{clientName}",
    choices: [
      { id: 'discount', label: 'Proposer -8 % sur le tarif (relance)',   effects: [{ kind: 'treasury', amount: -500 }, { kind: 'reputation', amount: 1 }] },
      { id: 'argue',    label: 'Argumenter qualité (pas de remise)',     effects: [{ kind: 'reputation', amount: 0 }] },
      { id: 'leave',    label: 'Laisser tomber',                         effects: [{ kind: 'reputation', amount: -2 }] },
    ],
    defaultChoiceId: 'argue',
  },

  // ── Tier CONFIANT (small thanks, gentle hints) ────────────────────────
  {
    id: 'cli-confiant-thanks-01', senderType: 'client', tone: 'friendly', kind: 'info',
    trigger: 'random', weight: 4, ttlDays: 6,
    tierFilter: ['confiant'],
    subjectTemplate: 'Petit mot de remerciement',
    bodyTemplate:
      "Bonjour,\n\nJe voulais juste vous dire que ce mois s'est très bien " +
      "passé. La régularité paie. Continuez comme ça.\n\n{clientName}",
  },
  {
    id: 'cli-confiant-renewal-hint-01', senderType: 'client', tone: 'formal', kind: 'info',
    trigger: 'random', weight: 3, ttlDays: 6, minDays: 90,
    tierFilter: ['confiant'],
    subjectTemplate: 'Renouvellement à anticiper',
    bodyTemplate:
      "Bonjour,\n\nNotre contrat actuel arrive bientôt à échéance. Nous " +
      "souhaitons le reconduire, sans surprise — pensez à anticiper le " +
      "renouvellement.\n\nDirection logistique — {clientName}",
  },

  // ── Tier FIDELE (warm praise, opportunity offers) ─────────────────────
  {
    id: 'cli-fidele-praise-01', senderType: 'client', tone: 'friendly', kind: 'info',
    trigger: 'random', weight: 4, ttlDays: 7,
    tierFilter: ['fidele'],
    subjectTemplate: 'Vous êtes notre transporteur préféré 💙',
    bodyTemplate:
      "Bonjour,\n\nMois après mois vous confirmez votre fiabilité. Nous " +
      "vous considérons désormais comme notre transporteur de référence. " +
      "Merci pour votre constance.\n\nDirection — {clientName}",
  },
  {
    id: 'cli-fidele-extra-business-01', senderType: 'client', tone: 'friendly', kind: 'decision',
    trigger: 'random', weight: 3, ttlDays: 5,
    tierFilter: ['fidele'],
    subjectTemplate: 'Une mission supplémentaire pour vous',
    bodyTemplate:
      "Bonjour,\n\nNous avons un volume additionnel ponctuel à livrer cette " +
      "semaine. Vous êtes notre premier choix — pouvez-vous l'absorber ?\n\n{clientName}",
    choices: [
      { id: 'yes',  label: 'Oui, on s\'organise (+800 €)', effects: [{ kind: 'treasury', amount: 800 }, { kind: 'reputation', amount: 2 }] },
      { id: 'part', label: 'Partiellement (+300 €)',       effects: [{ kind: 'treasury', amount: 300 }] },
      { id: 'no',   label: 'Désolé, on est saturé',         effects: [{ kind: 'reputation', amount: -1 }] },
    ],
    defaultChoiceId: 'part',
  },

  // ── Tier PARTENAIRE (gifts, recommendations, premium) ─────────────────
  {
    id: 'cli-partenaire-gift-01', senderType: 'client', tone: 'friendly', kind: 'info',
    trigger: 'random', weight: 2, ttlDays: 7,
    tierFilter: ['partenaire'],
    subjectTemplate: 'Cadeau d\'année — Bordeaux Grand Cru 🍷',
    bodyTemplate:
      "Bonjour,\n\nNous tenons à fêter notre partenariat. Vous recevrez un " +
      "carton de Bordeaux Grand Cru à votre dépôt cette semaine. À votre " +
      "santé !\n\nLe Président — {clientName}",
  },
  {
    id: 'cli-partenaire-recommendation-01', senderType: 'client', tone: 'friendly', kind: 'info',
    trigger: 'random', weight: 3, ttlDays: 7,
    tierFilter: ['partenaire'],
    subjectTemplate: 'Je vous ai recommandé à un grand compte',
    bodyTemplate:
      "Bonjour,\n\nJ'ai vanté votre sérieux auprès d'un grand compte qui " +
      "cherche un transporteur de confiance. Ils devraient vous contacter " +
      "sous peu — préparez votre meilleure offre.\n\n{clientName}",
  },
  {
    id: 'cli-partenaire-vip-event-01', senderType: 'client', tone: 'friendly', kind: 'decision',
    trigger: 'random', weight: 2, ttlDays: 6, minDays: 270,
    tierFilter: ['partenaire'],
    subjectTemplate: 'Invitation à notre soirée VIP',
    bodyTemplate:
      "Bonjour,\n\nNotre soirée annuelle réunit nos partenaires stratégiques. " +
      "Vous y êtes convié·e. Présence très appréciée — networking, contrats, " +
      "et bons moments.\n\nDirection — {clientName}",
    choices: [
      { id: 'yes',  label: 'Y aller (effet réseau +3)',   effects: [{ kind: 'reputation', amount: 3 }] },
      { id: 'send', label: 'Envoyer un collaborateur',     effects: [{ kind: 'reputation', amount: 1 }] },
      { id: 'no',   label: 'Décliner poliment',            effects: [{ kind: 'reputation', amount: 0 }] },
    ],
    defaultChoiceId: 'yes',
  },

  // ── Constructeurs enrichis ──────────────────────────────────────────────
  {
    id: 'sup-electric-01', senderType: 'supplier', tone: 'friendly', kind: 'info',
    trigger: 'random', weight: 2, ttlDays: 7, minDays: 180,
    subjectTemplate: 'Notre nouveau VOLVAX FX Electric',
    bodyTemplate:
      "Bonjour,\n\nNous commercialisons le FX Electric ZeroLoad — 100 % " +
      "électrique, autonomie 400 km. Aides à l'achat possibles. " +
      "Démo sur RDV.\n\n{senderName}",
  },
  // 05/02/2026 — Q1 retour user prod : ce message « Pneumatiques — offre
  // flotte » avait une décision « Souscrire (-1 200 €) » sans contre-partie
  // réelle (pas de bonus maintenance/usure derrière), et un « Décliner »
  // sans effet. Supprimé du pool random pour réduire le bruit Messagerie.
  // {
  //   id: 'sup-tire-01', senderType: 'supplier', tone: 'formal', kind: 'decision',
  //   trigger: 'random', weight: 3, ttlDays: 5,
  //   subjectTemplate: 'Pneumatiques — offre flotte',
  //   bodyTemplate:
  //     "Bonjour,\n\nNous proposons un contrat pneumatiques annualisé qui " +
  //     "couvre rotation, équilibrage et remplacement. 1 200 €/an, économies " +
  //     "estimées 30 %.\n\nManupneu",
  //   choices: [
  //     { id: 'sub',  label: 'Souscrire (1 200 €/an)', effects: [{ kind: 'treasury', amount: -1200 }] },
  //     { id: 'pass', label: 'Décliner',                effects: [] },
  //   ],
  //   defaultChoiceId: 'pass',
  // },
  // 05/02/2026 (Q1 Lot 1) — Retour user : ce message « Forfait entretien
  // tout compris » spammait sans impact réel (les 2 choix n'avaient AUCUN
  // effet). Supprimé du pool random pour réduire le bruit dans la Messagerie.
  // {
  //   id: 'sup-maintenance-pkg-01', senderType: 'supplier', tone: 'formal', kind: 'decision',
  //   trigger: 'random', weight: 2, ttlDays: 6, minDays: 120,
  //   subjectTemplate: 'Forfait entretien tout compris',
  //   bodyTemplate: ...
  //   choices: [
  //     { id: 'sub',  label: 'Étudier le forfait',        effects: [] },
  //     { id: 'pass', label: 'Garder notre maintenance',  effects: [] },
  //   ],
  //   defaultChoiceId: 'pass',
  // },

  // ── Presse / médias enrichis ────────────────────────────────────────────
  {
    id: 'media-tweet-01', senderType: 'media', tone: 'humour', kind: 'info',
    trigger: 'random', weight: 2, ttlDays: 4,
    // Requires an actively driving driver — otherwise the tweet wouldn't make sense.
    requires: { driverActive: true, vehicleActive: true },
    subjectTemplate: 'Un tweet viral mentionne {companyName}',
    bodyTemplate:
      "Bonjour,\n\nUn de vos chauffeurs a aidé une famille en panne sur " +
      "l'A6. Le post a fait 12 000 likes. Vous êtes mentionné avec une " +
      "photo de votre camion.\n\nVeille réseaux sociaux",
  },
  {
    id: 'media-criticism-01', senderType: 'media', tone: 'aggressive', kind: 'info',
    trigger: 'random', weight: 1, ttlDays: 5, minDays: 240,
    subjectTemplate: 'Article critique dans la presse régionale',
    bodyTemplate:
      "Bonjour,\n\nUn journaliste publie demain un article sur les conditions " +
      "de travail dans le transport régional. {companyName} y est cité (pas " +
      "particulièrement épinglé mais associé au lot).\n\nVeille presse",
  },
  {
    id: 'media-radio-01', senderType: 'media', tone: 'friendly', kind: 'decision',
    trigger: 'random', weight: 1, ttlDays: 5, minDays: 180,
    subjectTemplate: 'Invitation radio locale',
    bodyTemplate:
      "Bonjour,\n\nNotre radio locale invite des entrepreneurs sur sa " +
      "matinale. 10 minutes en direct sur l'avenir du transport en région.\n\n" +
      "RadioPro {city}",
    choices: [
      { id: 'yes',  label: 'Accepter (+2 notoriété)', effects: [{ kind: 'notoriety', amount: 2 }] },
      { id: 'pass', label: 'Refuser',                  effects: [] },
    ],
    defaultChoiceId: 'pass',
  },

  // ── Inspecteur / Conformité enrichis ────────────────────────────────────
  {
    id: 'insp-warning-01', senderType: 'inspector', tone: 'urgent', kind: 'decision',
    trigger: 'random', weight: 1, ttlDays: 4, minDays: 90,
    subjectTemplate: 'Manquement signalé — temps de pause',
    bodyTemplate:
      "Madame, Monsieur,\n\nUn courrier anonyme signale que vos chauffeurs " +
      "dépassent les temps de conduite autorisés. Une visite peut être " +
      "diligentée. Quelle réponse ?\n\nInspection du Travail",
    choices: [
      { id: 'audit',  label: 'Audit interne immédiat + plan',  effects: [{ kind: 'treasury', amount: -800 }, { kind: 'reputation', amount: 3 }] },
      { id: 'wait',   label: 'Attendre la visite',              effects: [{ kind: 'reputation', amount: -2 }] },
      { id: 'deny',   label: 'Nier en bloc',                     effects: [{ kind: 'reputation', amount: -5 }] },
    ],
    defaultChoiceId: 'wait',
  },

  // ── Actualités enrichies ────────────────────────────────────────────────
  {
    id: 'news-fuel-down-01', senderType: 'news', tone: 'friendly', kind: 'info',
    trigger: 'random', weight: 3, ttlDays: 4,
    subjectTemplate: 'Baisse du diesel à la pompe (-3%)',
    bodyTemplate:
      "Les prix du gazole baissent légèrement cette semaine. Bonne nouvelle " +
      "pour vos marges du mois.\n\n" +
      "Impact : -3 % sur votre conso carburant pendant 21 jours.\n\n" +
      "Actu Transport",
    autoEffects: [{ kind: 'fuelPriceShock', multiplier: 0.97, durationDays: 21, source: 'fuel-down' }],
  },
  {
    id: 'news-tech-01', senderType: 'news', tone: 'neutral', kind: 'info',
    trigger: 'random', weight: 2, ttlDays: 7,
    subjectTemplate: 'Plate-forme de fret 100 % digitale',
    bodyTemplate:
      "Un nouvel acteur lance une marketplace pour le fret régional. Les " +
      "PME y trouvent leur compte selon les premiers retours.\n\nActu Transport",
  },
  {
    id: 'news-shortage-01', senderType: 'news', tone: 'urgent', kind: 'info',
    trigger: 'random', weight: 2, ttlDays: 4,
    subjectTemplate: 'Pénurie de chauffeurs PL en France',
    bodyTemplate:
      "Selon la FNTR, le manque de chauffeurs PL atteint 50 000 postes " +
      "vacants. Bonne nouvelle pour la valorisation des conducteurs en place.\n\n" +
      "Actu Transport",
  },
  {
    id: 'news-eu-deal-01', senderType: 'news', tone: 'neutral', kind: 'info',
    trigger: 'random', weight: 2, ttlDays: 6,
    subjectTemplate: 'Accord transport UE — simplification',
    bodyTemplate:
      "Bruxelles simplifie les formalités d'envoi à l'international. Le " +
      "secteur respire. Bonne occasion pour développer l'export.\n\n" +
      "UE Transports",
  },
  {
    id: 'news-weather-fc-01', senderType: 'news', tone: 'neutral', kind: 'info',
    trigger: 'random', weight: 3, ttlDays: 3,
    subjectTemplate: 'Météo pro — vigilance orange demain',
    bodyTemplate:
      "Météo-France annonce des orages violents sur le quart sud-est demain. " +
      "Anticipez les tournées sensibles.\n\nMétéo Logistique",
  },

  // ── Système / Bilans automatiques ───────────────────────────────────────
  {
    id: 'sys-month-recap-01', senderType: 'system', tone: 'neutral', kind: 'info',
    trigger: 'random', weight: 2, ttlDays: 10,
    subjectTemplate: 'Mémo : préparation clôture mensuelle',
    bodyTemplate:
      "Bonjour,\n\nFin de mois dans quelques jours. Pensez à vérifier vos " +
      "factures émises et à provisionner les charges sociales.\n\nL'équipe HST",
  },
  {
    id: 'sys-tip-01', senderType: 'system', tone: 'friendly', kind: 'info',
    trigger: 'random', weight: 3, ttlDays: 7,
    subjectTemplate: '💡 Astuce — surveillez vos marges',
    bodyTemplate:
      "Conseil du jour : avec un commercial + le TMS débloqué, vous voyez " +
      "la marge brute exacte de chaque contrat dans le Dashboard. Évitez " +
      "les contrats à marge négative !\n\nL'équipe HST",
  },
  {
    id: 'sys-onboarding-01', senderType: 'system', tone: 'friendly', kind: 'info',
    trigger: 'random', weight: 2, ttlDays: 14, maxDays: 90,
    subjectTemplate: 'Les Fonctions Support, vous y avez pensé ?',
    bodyTemplate:
      "Bonjour,\n\nEn recrutant des commerciaux, RH, comptables, vous " +
      "débloquez des bonus passifs et des fonctionnalités (calcul de " +
      "marge, veille concurrentielle…). À explorer dans /fonctions-support !\n\n" +
      "L'équipe HST",
  },
];

/**
 * Index by trigger for fast lookup.
 * @returns {Object<string, MessageTemplate[]>}
 */
export const groupByTrigger = () => {
  const map = {};
  MESSAGE_TEMPLATES.forEach(t => {
    if (!map[t.trigger]) map[t.trigger] = [];
    map[t.trigger].push(t);
  });
  return map;
};

/**
 * Pick a random template among `random` triggers, respecting weights.
 * Returns null if no template matches the conditions.
 *
 * `stateContext` may include:
 *   - totalDays, phase
 *   - driverCount, vehicleActiveCount, activeContractsCount
 *
 * In addition to explicit `requires` declared on templates, this helper does
 * an automatic placeholder analysis: any template referencing `{driverName}`
 * / `{driverFirstName}` / `{driverLevel}` is rejected when there is no
 * driver, and any template using `{clientName}` is rejected when there is no
 * active contract — regardless of senderType. That prevents cross-sender
 * "ghost messages" (e.g. competitor template mentioning a driver while the
 * player has none).
 */
export const pickRandomTemplate = (stateContext) => {
  const driverCount = stateContext.driverCount || 0;
  const vehicleActiveCount = stateContext.vehicleActiveCount || 0;
  const activeContractsCount = stateContext.activeContractsCount || 0;
  // `clientLoyaltyTiers` is an array of tier ids currently held by at least
  // one recurring client (e.g. ['neutre','confiant']). Provided by the
  // messageSlice picker. If undefined, all tiers are assumed open so we
  // don't accidentally hide templates in early game / tests.
  const tiers = stateContext.clientLoyaltyTiers || null;
  const candidates = MESSAGE_TEMPLATES.filter(t => {
    if (t.trigger !== 'random') return false;
    if (t.minDays && (stateContext.totalDays || 0) < t.minDays) return false;
    if (t.maxDays && (stateContext.totalDays || 0) > t.maxDays) return false;
    if (t.minPhase && (stateContext.phase || 0) < t.minPhase) return false;
    if (t.senderType === 'driver' && driverCount === 0) return false;
    if (t.senderType === 'client' && activeContractsCount === 0) return false;
    if (t.requires) {
      if (t.requires.driverActive && driverCount === 0) return false;
      if (t.requires.vehicleActive && vehicleActiveCount === 0) return false;
      if (t.requires.activeContract && activeContractsCount === 0) return false;
      // 02/06/2026 — Gate templates by REAL game state (treasury level, team
      // size). Prevents false-positive narrative (e.g. "comptes dans le rouge"
      // when treasury > 0, or "point équipe" when there's no real team yet).
      if (typeof t.requires.treasuryBelow === 'number'
          && (stateContext.treasury || 0) >= t.requires.treasuryBelow) return false;
      if (typeof t.requires.driverCountMin === 'number'
          && driverCount < t.requires.driverCountMin) return false;
    }
    // Tier filter — only fire client templates whose target tier is currently
    // populated (P1 narrative relationship system, 27/05/2026).
    if (t.tierFilter && Array.isArray(t.tierFilter) && t.tierFilter.length > 0) {
      if (tiers && !t.tierFilter.some(x => tiers.includes(x))) return false;
    }
    const fullText = `${t.subjectTemplate || ''} ${t.bodyTemplate || ''}`;
    if (/\{driver(Name|FirstName|Level)\}/.test(fullText) && driverCount === 0) return false;
    if (/\{clientName\}/.test(fullText) && activeContractsCount === 0) return false;
    return true;
  });
  if (candidates.length === 0) return null;
  const totalWeight = candidates.reduce((s, t) => s + (t.weight || 1), 0);
  let roll = Math.random() * totalWeight;
  for (const t of candidates) {
    roll -= (t.weight || 1);
    if (roll <= 0) return t;
  }
  return candidates[candidates.length - 1];
};

export const SENDER_TYPES = [
  'driver', 'client', 'supplier', 'bank', 'competitor',
  'urssaf', 'media', 'inspector', 'hr', 'system', 'news',
];

export const SENDER_TYPE_LABELS = {
  driver:     'Salariés',
  client:     'Clients',
  supplier:   'Constructeurs',
  bank:       'Banque',
  competitor: 'Concurrents',
  urssaf:     'URSSAF / Douanes',
  media:      'Presse pro',
  inspector:  'Inspecteur',
  hr:         'RH interne',
  support:    'Fonctions support',
  system:     'Système',
  news:       'Actualités',
  supervisor: 'Responsable pédagogique',
};
